import React, { useContext, useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Navigate, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';

import AccessTokenExpiredMessageModal from './components/AccessTokenExpiredMessageModal';
import { preloadCardDeck } from './components/Card/deck';
import PerfHud from './components/Debug/PerfHud';
import { LobbyLabMount } from './components/LobbyLab';
import { getDragState, subscribeDragPhase } from './components/RebindingTableView/tableSwipeDrag';
import { SfxLabMount } from './components/SfxLab';
import DuplicateScreenModal from './components/UserActions/DuplicateScreenModal';
import ErrorMsgModal from './components/UserActions/ErrorMsgModal';
import IntegrityBlockModal from './components/UserActions/IntegrityBlockModal';
import installAudioIdleSuspend from './utils/audioIdleSuspend';
import { admitTableFrames } from './utils/frameOrder';
import { reportFrameOrderValve, reportOutOfOrderFrame } from './utils/frameOrderReport';
import installGsapIdleSleep from './utils/gsapIdleTicker';
import { BackgroundTableBuffer, compactTablePayloads } from './utils/tablePayloadQueue';

/**
 * Redirects legacy deep links like /game/123/type/texas to the new URL format.
 */
const GameDeepLinkRedirect = () => {
  const params = useParams();
  const gameID = params.gameID || '';
  const gameType = params.gameType || 'texas';
  return <Navigate to={`/table/${gameID}/${gameType}`} replace />;
};
import AnnouncedTournamentTableExit from './components/AnnouncedTournamentTableExit/AnnouncedTournamentTableExit';
import { CashierProvider } from './components/Cashier/CashierContext';
import CashierPanel from './components/Cashier/CashierPanel';
import ConnectionWarning from './components/ConnectionWarning';
import BenchmarkTableBridge from './components/Debug/BenchmarkTableBridge';
import DebugConsole from './components/DebugConsole';
import LobbyOverlayLock from './components/ForcePortrait/LobbyOverlayLock';
import NativeOrientationLock from './components/ForcePortrait/NativeOrientationLock';
import BiometricEnrollmentPrompt from './components/Login/BiometricEnrollmentPrompt';
import BiometricUnlockPrompt from './components/Login/BiometricUnlockPrompt';
import MaintenanceNotice from './components/Login/MaintenanceNotice';
import LoginRequiredModal from './components/LoginPrompt/LoginRequiredModal';
import HeadsUpIntroAutoPrompt from './components/HeadsUpIntro/HeadsUpIntroAutoPrompt';
import MultiRunIntroAutoPrompt from './components/MultiRunIntro/MultiRunIntroAutoPrompt';
import MultiTableGrid from './components/MultiTableGrid/MultiTableGrid';
import PromoEntryAutoPrompt from './components/PlayerPromos/PromoEntryAutoPrompt';
import PromoWeeklyResetRefresh from './components/PlayerPromos/PromoWeeklyResetRefresh';
import PostLoginTablesRestore from './components/PostLoginTablesRestore/PostLoginTablesRestore';
import SitGoResultWatcher from './components/SitGoResult/SitGoResultWatcher';
import SlideContainer from './components/SlideContainer/SlideContainer';
import { useSupportContext } from './components/Support/SupportContext';
import SupportPanel from './components/Support/SupportPanel';
import SeatOfferTimeoutSnackbar from './components/Waitlist/SeatOfferTimeoutSnackbar';
import WaitlistAlertSound from './components/Waitlist/WaitlistAlertSound';
import {
  CREATE_PLAYER_RESPONSE,
  GET_ACTIVE_SCROLLING_NOTIFICATIONS,
  GET_PLAYER_PROMOS,
  GOT_DEVICE_CONNECTED,
  GOT_DUPLICATE_ACCESS,
  GOT_JOIN_GAME,
  GOT_LIVE_CHAT_NOTIFICATION,
  GOT_LOCATION,
  GOT_LOGIN,
  GOT_MAINTENANCE_SCHEDULE,
  GOT_OMAHA,
  GOT_PLAYER_GAMES,
  GOT_PLAYER_PROMOS,
  GOT_PLAYERTOURNAMENTDETAILS,
  GOT_REFRESH_TOKEN,
  GOT_SCROLLING_NOTIFICATIONS,
  GOT_SERVER_TIME,
  GOT_SUPPORT_MESSAGE,
  GOT_TEXAS,
  GOT_TOURNAMENT_STATUS,
  GOT_TOURNAMENT_UNREGISTER,
  GOT_VERIFY_PRIVATE_TABLE_PASSWORD,
  REGISTER_SOCKET,
  UNSUBCRIBE_TO_GAME_UPDATES
} from './constants/eventConstants';
import { DualSocketContext as SocketContext } from './contexts/DualSocketContext';
import { GameProvider } from './contexts/GameContext';
import GlobalContext from './contexts/GlobalContext';
import LobbyContext from './contexts/LobbyContext';
import { ScaleContextProvider } from './contexts/ScaleContext';
import SplashScreenContext from './contexts/SplashScreenContext';
import { ViewManagerProvider } from './contexts/ViewManagerContext';
import { useBiometricEnrollmentOffer } from './hooks/useBiometricEnrollmentOffer';
import useBootstrapSessionRecovery from './hooks/useBootstrapSessionRecovery';
import useColluderRestrictionsSync from './hooks/useColluderRestrictionsSync';
import useStreakSync from './components/Streaks/state/useStreakSync';
import { useDropMomentInLobby } from './components/Streaks/Table/StreakTableMoment';
import { useGuest } from './hooks/useGuest';
import { useNativeLoginDeepLink } from './hooks/useNativeLoginDeepLink';
import useOpenWaitlistedTables from './hooks/useOpenWaitlistedTables';
import { usePendoIdentify } from './hooks/usePendoIdentify';
import usePlayerMoneySocketSync from './hooks/usePlayerMoneySocketSync';
import useWaitlistSocketSync from './hooks/useWaitlistSocketSync';
import ConfigPage from './pages/ConfigPage';
import DemoTable from './pages/DemoTable';
import Game from './pages/Game';
import HandHistoryPreview from './pages/HandHistoryPreview';
import GuestLobby from './pages/Lobby/GuestLobby';
import Lobby from './pages/Lobby/Lobby';
import { showIntegrityBlock } from './reducers/colluderRestrictions';
import {
  clearTablePlayers,
  mergeTournamentStatus,
  setSeatJoinError,
  updateCurrentPlayerGUID,
  updateCurrentTable,
  updateTournamentStatus
} from './reducers/currentTable';
import { updateLiveChatNotifications } from './reducers/liveChatNotifications';
import {
  removeClosedCashGame,
  removePlayerTournamentDetail,
  updatePlayerTournamentDetails
} from './reducers/lobby';
import { setMaintenanceSchedule } from './reducers/maintenanceSchedule';
import { updateScrollingNotifications } from './reducers/notifications';
import { updatePlayerGames } from './reducers/playerGames';
import { updatePlayerPromos } from './reducers/playerPromos';
import { setServerDetails } from './reducers/serverDetails';
import { removeTable } from './reducers/tables';
import { updateErrorMsg, updateUser } from './reducers/user';
import { clearWaitlistForGame } from './reducers/waitlist';
import { AF_EVENT, initAppsFlyer, logAppsFlyerEvent } from './services/appsflyer';
import useAuthService from './services/AuthService';
import useGameService from './services/GameService';
import { initOneSignal, loginOneSignal, storeOneSignalDevice } from './services/OneSignalService';
import { clearPendingSeatJoin, resolveAndRetireSeatJoinError } from './services/pendingSeatJoin';
import { setPendoGuidesEnabled } from './services/PendoService';
import { isIntegrityBlock } from './types/colluder';
import { deriveActiveView } from './utils/activeView';
import {
  canAttemptRefresh,
  cleanUpHardReloadParam,
  clearAuth,
  consumeLoginReturnPath,
  decodeJwtPayload,
  getAccessToken,
  getAuthToken,
  hardReload,
  hasValidAuthToken,
  isSessionEndTransition,
  peekTournamentIntent,
  readAccessTokenExp,
  startTokenRefresh,
  stopTokenRefresh,
  verifyRefreshNow
} from './utils/auth';
import {
  getClosedCashTableUpdate,
  getPendingCashTableUpdate,
  shouldSweepClosedTable
} from './utils/closedTableSweep';
import { decompressData } from './utils/functions';
import { isReplaying, recordFrame } from './utils/handRecorder';
import { computeLocationEventUpdates } from './utils/locationCheck';
import {
  clearProtectedTable,
  clearReconciledAwayTable,
  isProtectedTable,
  isRecentlyExitedTable,
  isReconciledAwayTable,
  markRecentlyExitedTable,
  markReconciledAwayTable,
  reconcileExitsWithPlayerGames,
  releaseExitsForAssignedSeats
} from './utils/recentlyExitedTables';
import { subscribeSitGoUnregisterSettlement } from './utils/sitGoUnregisterInFlight';
import {
  computeActiveTournamentRedirect,
  computeActiveTournamentRedirectFromSeat,
  computeObservedTablesToClose,
  shouldHoldTableThroughRebalance
} from './utils/tournamentNavigation';
import {
  computeSubscribedTournamentIds,
  visibleTablesForActiveView
} from './utils/tournamentSubscriptions';
import { APP_DOMAIN, DESCOPE_COOKIE } from './utils/urls';
import { BUNDLE_VERSION, loadAppVersions } from './utils/version';
import { useFeatureFlag, useFeatureFlagExposure } from './featureFlags';
import { useAppDispatch, useAppSelector } from './hooks';

import './App.scss';

// Clear auth state and return to login. Delegates to clearAuth() (utils/auth)
// so the member-key clear list has a single source of truth and can't drift
// between the logout-button path and this token-expiry path. hardReload()
// (not a plain location.reload()) forces a genuinely fresh fetch rather than
// risking a cached stale page/bundle right after ending the session.
const clearAuthAndLogout = () => {
  clearAuth();
  hardReload();
};

// The live inputs App.tsx's stale-table sweeps feed shouldHoldTableThroughRebalance
// (see there): the engine's closed set, and the player's recent seats per
// tournament (recentAssignedSeatsRef).
const holdsTableThroughRebalance = (
  gid: string,
  details: any[] | undefined,
  tables: Record<string, any> | undefined,
  recentAssignedSeats: Map<number, Map<number, number>>,
  closedGameIds: Set<string>
): boolean => {
  const tournId = Number(tables?.[gid]?.game?.tournamentId);
  return shouldHoldTableThroughRebalance(gid, details, tables, {
    engineClosed: closedGameIds.has(gid),
    wasMySeat:
      Number.isFinite(tournId) && recentAssignedSeats.get(tournId)?.has(Number(gid)) === true,
    isProtected: isProtectedTable(gid)
  });
};

const getScrollingNotificationsFromPayload = (data: any) => {
  const parsed = decompressData(data);

  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.notifications)) return parsed.notifications;
  if (Array.isArray(parsed?.data)) return parsed.data;
  if (Array.isArray(parsed?.data?.notifications)) return parsed.data.notifications;
  if (Array.isArray(parsed?.response?.notifications)) return parsed.response.notifications;

  return null;
};
// Recently-exited-table tracking moved to ./utils/recentlyExitedTables for
// testability — see the file's docblock for the full contract. App.tsx still
// owns the auto-resub effect that reads + prunes the same localStorage record
// (line ~440 below).

// Error/reliability telemetry. Env-gated: with no REACT_APP_SENTRY_DSN set,
// Sentry.init is never called, so every Sentry.captureException / setUser below
// (and in GlobalErrorBoundary) stays a no-op — inert in any environment that
// hasn't opted in. Deliberately minimal (error capture only, no session replay
// or full tracing) to avoid adding runtime overhead to a real-time game client;
// this exists so the silent-lockup / render-crash rate becomes measurable,
// which it currently is not (init was commented out entirely).
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT || 'localhost',
    // The BUNDLE version, not the native shell version: a stack trace belongs
    // to the JS that was running, and OTA moves that independently of the
    // installed app. REACT_APP_RELEASE is not set by any build, so this used
    // to tag every event '1.0'.
    release: process.env.REACT_APP_RELEASE || BUNDLE_VERSION,
    tracesSampleRate: 0,
    enabled: true
  });
  // The native shell goes on as tags instead, so a report says which app
  // build hosted the bundle without the two being conflated into one number.
  Sentry.setTag('bundleVersion', BUNDLE_VERSION);
  loadAppVersions()
    .then(({ nativeVersion, nativeBuild }) => {
      if (nativeVersion) Sentry.setTag('appVersion', nativeVersion);
      if (nativeBuild) Sentry.setTag('appBuild', nativeBuild);
    })
    // bundleVersion is already tagged above, so a failed native read costs a
    // tag, not a report. It must not become an unhandled rejection at boot.
    .catch(() => {});
}

function App() {
  // Sleep GSAP's rAF loop whenever no tween is active - an awake ticker keeps
  // the WebView producing full-rate frames of nothing (see utils/gsapIdleTicker).
  useEffect(() => installGsapIdleSleep(), []);
  // Card faces are composed in-bundle (Card/deck.ts); the back is the one
  // fetched artwork. Warm it when the browser is idle rather than on a timer
  // that landed in the socket-connect window (review #3836); the timeout keeps
  // it from waiting forever on a busy main thread, and the setTimeout branch
  // covers Safari, which has no requestIdleCallback.
  useEffect(() => {
    const idle = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof idle.requestIdleCallback === 'function') {
      const id = idle.requestIdleCallback(preloadCardDeck, { timeout: 4000 });
      return () => idle.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(preloadCardDeck, 1500);
    return () => window.clearTimeout(id);
  }, []);
  // Suspend the audio context after a quiet spell - an idle-but-running
  // context keeps a native mixer thread at ~10% of a core. Deliberately
  // reconciled with EN-10512's prompt-audio work (utils/audioResume), which
  // shares the same context: see utils/audioIdleSuspend for the contract
  // (honest Howler state flag, 15s window, immediate suspend when muted).
  useEffect(() => installAudioIdleSuspend(), []);

  const { setIsLogged } = useContext(LobbyContext);
  const { socket } = useContext(SocketContext);
  const {
    isLocationInvalid,
    committedGeoLegacy,
    setIsLocated,
    setIsLocationInvalid,
    setInvalidLocationMsg,
    setOpenAccessTokenModal: _setOpenAccessTokenModal,
    setDeviceId,
    setDuplicateAccessMsg,
    setIsDuplicateAccess,
    setDuplicateAccessGameID,
    setDuplicateAccessGame,
    setOpenAskBuyin,
    setOpenAskPassword,
    setAskPasswordMsg
  } = useContext(GlobalContext);
  const playerEmail = useAppSelector((state) => state.user.playerEmail);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  usePendoIdentify();
  // Pause Pendo's guide scanning ONLY while a table-swipe gesture is in
  // flight (see setPendoGuidesEnabled) - its 30-80ms DOM sweeps landing
  // mid-gesture read as jitter. Guides stay enabled on tables otherwise
  // (product plans in-table guides). Restarting is the expensive half:
  // startGuides() re-runs Pendo's FULL guide-targeting scan (measured as the
  // getComputedStyle storm - ~330ms of a 6-swipe loop's stall time, A/B'd
  // against stopGuides on device), so a 500ms-after-idle restart scheduled a
  // scan into every pause between swipes, colliding with the next gesture.
  // Restart only after a real lull, and inside an idle slice so a queued
  // gesture still beats it; a new gesture cancels both stages. Analytics are
  // untouched throughout - only the guide engine pauses (verified: teardown
  // vs stopGuides traced identically).
  // The only cost of a longer lull is guide-entrance latency: a player who
  // just stopped swiping sees a pending guide this much later. Nothing is
  // missed - the scan runs when the lull elapses. 15s per product call
  // (2026-08-20): no guides are in use today, so the engine only needs to be
  // awake enough that a future guide still shows up; revisit if a guide ever
  // needs a snappy entrance after table swipes.
  const GUIDE_RESTART_LULL_MS = 15_000;
  useEffect(() => {
    let restartId: number | null = null;
    let idleId: number | null = null;
    const cancelRestart = () => {
      if (restartId !== null) window.clearTimeout(restartId);
      restartId = null;
      const ric = (window as any).cancelIdleCallback;
      if (idleId !== null && typeof ric === 'function') ric(idleId);
      idleId = null;
    };
    const off = subscribeDragPhase((state) => {
      if (state.phase !== 'idle') {
        cancelRestart();
        setPendoGuidesEnabled(false);
        return;
      }
      restartId = window.setTimeout(() => {
        restartId = null;
        const ric = (window as any).requestIdleCallback;
        if (typeof ric === 'function') {
          idleId = ric(() => setPendoGuidesEnabled(true), { timeout: 2000 });
        } else {
          setPendoGuidesEnabled(true);
        }
      }, GUIDE_RESTART_LULL_MS);
    });
    return () => {
      off();
      cancelRestart();
      setPendoGuidesEnabled(true);
    };
  }, []);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authService = useAuthService();
  const gameService = useGameService();
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const game = useAppSelector((state) => state.currentTable.game);

  // The table currently shown on screen (URL-derived). Only this table's frames
  // update the shared `currentTable` slice; other open tables update only their
  // own `tables[gid]` entry. This keeps the App root (which subscribes to
  // `currentTable.game` just above) re-rendering once per animation frame instead
  // of once per open table — with 4 tables observed, that was a 4× App-root churn.
  // A ref (not the value) because the socket effect's flush closure is long-lived.
  const focusedTableMatch = useMatch('/table/:gameID/:gameType');
  const focusedGameIdRef = useRef<string>('');
  useEffect(() => {
    focusedGameIdRef.current = focusedTableMatch?.params.gameID ?? '';
  }, [focusedTableMatch]);

  // Scrub hardReload()'s cache-busting `_hardReload` param from the address
  // bar now that this mount IS the fresh page it forced — see
  // cleanUpHardReloadParam's docblock in utils/auth.
  useEffect(() => {
    cleanUpHardReloadParam();
  }, []);

  // Refs keep the latest Redux state accessible inside socket.on callbacks
  // (whose closures otherwise capture stale values). The GOT_PLAYER_GAMES
  // handler below reads these to filter out wrong tournament tables — the
  // tables an active player ('P' in tournament X) shouldn't be subscribed
  // to because they belong to the same tournament as their assigned seat
  // but at a different gameID. Without filtering here, App.tsx's auto-resub
  // keeps re-subscribing to them after our per-table cleanup fires.
  const playerTournamentDetailsForFilter = useAppSelector(
    (state) => state.lobby.playerTournamentDetails
  );
  const tablesForFilter = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  // Stable, sorted CSV of the tournamentIds the player currently needs the
  // gotTnmStatus feed for. Drives the centralized tournament-feed subscription
  // below. Deriving a primitive string (not the objects) means the subscription
  // effect only re-runs when the SET of tournaments actually changes — not on
  // every per-table frame.
  //
  // The subscribe set is derived exclusively from OPEN TABLES the player is
  // currently viewing (Rule 2, focused-visibility). Neither a
  // playerTournamentDetails record on its own (retired Rule 1) nor an open
  // tournament-info panel (independently ref-counted via
  // acquire/releaseTournamentSub) contributes here.
  //
  // Focused-visibility rule (product spec):
  // A backgrounded tournament table (loaded in redux but NOT the URL's active
  // view) does not keep its tournament subscribed. Navigating from the table
  // to the lobby drops the subscribe set immediately; navigating back re-
  // subscribes. (A brief lobby dip now costs one unsubscribe + one subscribe
  // on the wire — the previous 30 s deferred grace was removed after the
  // App-level path became set-based, since in-tournament rebalancing no
  // longer produces a transient count-zero window.)
  // Grid view exposes every open table simultaneously → all of them count.
  // Table view (single) exposes only the URL-focused cell.
  // Lobby view exposes none.
  const location = useLocation();
  // Derived via the shared helper (utils/activeView) so this stays in lockstep
  // with ViewManagerContext's own derivation — App.tsx sits ABOVE that provider
  // and can't consume it directly, but the two must never classify the same
  // URL differently. Prefix-matches '/grid' so any suffix (trailing slash,
  // future grid sub-route) still counts as grid instead of collapsing to
  // 'lobby' and dropping all tournament feeds.
  const activeView = deriveActiveView(location.pathname, focusedTableMatch);
  const focusedGameIdForVisibility = focusedTableMatch?.params.gameID ?? '';

  // useMemo so the selector re-runs on activeView / focused-gameID changes
  // (URL navigation) in addition to Redux state changes. computeSubscribed-
  // TournamentIds returns SORTED, so the joined string is a stable memo key
  // for the subscribe effect below — it only re-runs when the SET actually
  // changes, not on every per-tick redux update. `tablesForFilter` (declared
  // above, at :250) is reused as the source of truth — the previous separate
  // `allTables` selector was byte-identical to it.
  const openTournamentIdsKey = React.useMemo(
    () =>
      computeSubscribedTournamentIds(
        visibleTablesForActiveView(activeView, focusedGameIdForVisibility, tablesForFilter)
      ).join(','),
    [tablesForFilter, activeView, focusedGameIdForVisibility]
  );

  // Refs for the global GOT_TOURNAMENT_UNREGISTER cleanup — the listener lives
  // inside the socket effect closure but needs the latest values of these
  // slices to (a) determine if the unregistered tournament was an SNG (via
  // sitAndGoList lookup by tournamentId) and (b) filter the unregistered
  // gameID out of state.playerGames.
  const sitAndGoListForFilter = useAppSelector((state) => state.lobby?.tournament?.sitAndGoList) as
    | any[]
    | undefined;
  const playerGamesForFilter = useAppSelector((state) => state.playerGames);
  const playerTournamentDetailsRef = useRef<any[]>([]);
  // Recent status:'P' seat assignments per tournament, timestamped. Populated
  // synchronously by the GOT_PLAYERTOURNAMENTDETAILS handler, PRUNED lazily by
  // a grace window. Its purpose: distinguish "table the player was recently
  // seated at" (balancing source — must NOT be reconciled away) from
  // "table the player is only observing" (Re-Entry target for the observer
  // cleanup that computeObservedTablesToClose was designed for).
  //
  // Why a dedicated ref instead of playerTournamentDetailsRef: that ref is
  // updated (via useEffect on the redux slice) AFTER each gotPlayer-
  // TournamentDetails dispatch, so by the second push of the same balancing
  // move it already contains the NEW assignment — the "before" comparison
  // reads new-vs-new and the balancing source gets removed on the follow-up
  // push (reported via [BALANCE-DEBUG] logs). This ref keeps a rolling
  // window of assignments across pushes so a gid that was the player's seat
  // one tick ago is still remembered as such N seconds later.
  const recentAssignedSeatsRef = useRef<Map<number, Map<number, number>>>(new Map());
  // Latest engine-reported closedGameIDs across all active tournaments. Kept as
  // a ref so any socket handler in App.tsx can consult it without racing the
  // one that built it (the gotTnmStatus handler around line 1173). Used by the
  // gotPlayerTournamentDetails reconciliation to override client-side
  // protections (wasMySeat, destination-not-hydrated, isProtectedTable) when
  // the engine has explicitly closed a gid — the engine is authoritative and
  // the client must move the player off a closed cell regardless of any
  // client-side balancing bookkeeping (reported bug: stuck on closed table for
  // 30 s after a failed rebalance).
  const closedGameIdsRef = useRef<Set<string>>(new Set());
  // Latest maintenance flag for socket-closure handlers (which capture stale
  // state). During maintenance the backend can bounce the token; we must NOT
  // drop the player to the login page — the maintenance page should show
  // instead (Unity parity).
  const isMaintenanceRef = useRef(false);
  const playerGUIDRef = useRef('');
  const tablesRef = useRef<Record<string, any>>({});
  const sitAndGoListRef = useRef<any[]>([]);
  const playerGamesRef = useRef<any[]>([]);
  // Tournaments we currently hold a centralized subscription for (see effect
  // below). Diffed against openTournamentIdsKey to add/drop subscriptions.
  const subscribedTournamentsRef = useRef<Set<string>>(new Set());
  // Keyboard runs in overlay mode (capacitor Keyboard.resize = 'none') so the
  // fixed table layout never reflows. The trade-off: a focused input inside a
  // scrollable container (cashier, login, profile, etc.) could sit under the
  // keyboard. When the keyboard opens, scroll the focused field into view —
  // a no-op for fixed elements (chat / bet tray) which lift themselves.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const keyboardOpen = () => window.innerHeight - vv.height - vv.offsetTop > 120;

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      // Wait for the keyboard animation to settle, then bring the field up.
      window.setTimeout(() => {
        if (keyboardOpen()) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    };

    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  useEffect(() => {
    isMaintenanceRef.current = isMaintenance;
  }, [isMaintenance]);

  useEffect(() => {
    playerGUIDRef.current = playerGUID || '';
  }, [playerGUID]);
  // EN-11178: read by the socket flush, which outlives renders.
  const frameOrderReport = useFeatureFlag('frameOrderReport');
  const frameOrderReportRef = useRef(frameOrderReport);
  useEffect(() => {
    frameOrderReportRef.current = frameOrderReport;
  }, [frameOrderReport]);
  const tableFrameOrder = useFeatureFlag('tableFrameOrder');
  const tableFrameOrderRef = useRef(tableFrameOrder);
  useEffect(() => {
    tableFrameOrderRef.current = tableFrameOrder;
  }, [tableFrameOrder]);
  // The decision shows on the felt: a player sees which frames were applied, on
  // the focused table and on every table in the grid alike.
  useFeatureFlagExposure('tableFrameOrder', 'table', activeView !== 'lobby');
  useEffect(() => {
    playerTournamentDetailsRef.current = playerTournamentDetailsForFilter || [];
  }, [playerTournamentDetailsForFilter]);
  useEffect(() => {
    tablesRef.current = tablesForFilter || {};
  }, [tablesForFilter]);
  useEffect(() => {
    sitAndGoListRef.current = sitAndGoListForFilter || [];
  }, [sitAndGoListForFilter]);
  useEffect(() => {
    playerGamesRef.current = (playerGamesForFilter || []) as any[];
  }, [playerGamesForFilter]);

  // Centralized tournament-feed subscription. Keep each tournament subscribed
  // for as long as the player is CURRENTLY VIEWING at least one of its tables
  // (see visibleTablesForActiveView + openTournamentIdsKey above). Unsubscribe
  // when the last such table falls out of the visible set — GameService's
  // shared ref-count still keeps the feed alive if an info modal or another
  // grid cell holds it. This replaces the per-table TournamentScreenModal
  // subscribe/unsubscribe, which mounted/unmounted (and so subscribed/
  // unsubscribed) on every bust / re-enter / rebalance / observe and kept
  // dropping gotTnmStatus mid-tournament in multi-table view. Driven by the
  // stable set of tournamentIds (openTournamentIdsKey), so churn of
  // individual tables within a tournament — an A→B balance where both share
  // the same tournamentId — leaves the joined key unchanged and never toggles
  // the feed.
  useEffect(() => {
    const current = new Set(openTournamentIdsKey ? openTournamentIdsKey.split(',') : []);
    const prev = subscribedTournamentsRef.current;
    // Subscribe every tournament that just entered the keep-subscribed set.
    current.forEach((tid) => {
      if (!prev.has(tid)) {
        // eslint-disable-next-line no-console
        console.log('[TNM-SUB] subscribe', tid, '| key=', openTournamentIdsKey);
        gameService.subscribeTournamentDetails(tid);
      }
    });
    // Unsubscribe every tournament that fell out of the set — i.e. the player
    // has no open table for it. The set-based diff here means an in-tournament
    // balance A→B produces an unchanged joined key ({tournamentId: same}) and
    // this effect doesn't fire mid-move at all. GameService.unsubscribeTournament-
    // Details is ref-counted, so an unmounting single holder decrements the
    // count; the wire unsubscribe only emits when the count hits 0 (all holders
    // — including a still-open info panel — have released).
    prev.forEach((tid) => {
      if (current.has(tid)) return;
      // eslint-disable-next-line no-console
      console.log('[TNM-SUB] UNSUBSCRIBE', tid, '| key=', openTournamentIdsKey);
      gameService.unsubscribeTournamentDetails(tid);
    });
    subscribedTournamentsRef.current = current;
  }, [openTournamentIdsKey, gameService]);

  // Re-assert tournament subscriptions when the SINGLE-TABLE view navigates to a
  // different table (its URL gameID changes — e.g. rebalancing relocates the
  // player, or a busted player is moved). The serverless tournament broadcaster
  // fans out to connections in the tournament room and drops any it fails to send
  // to (cleanupConnection); a navigation can transiently break the WebSocket send
  // mid-move, so the connection is silently removed from the room with NO
  // disconnect/connect cycle to trigger ConnectionManager.resubscribeAll — and the
  // centralized effect above doesn't re-fire because the tournamentId set is
  // unchanged. gotTnmStatus then stops (the reported single-table freeze on stale
  // isPostBalancing). Re-emitting subscribeTournament re-adds the connection to the
  // room. Grid never navigates (stays on /grid), so the focused gameID doesn't
  // change there and this is a no-op — matching the working multi-table behavior.
  //
  // IMPORTANT — only re-assert on a TABLE-TO-TABLE navigation (prev focused id
  // was already non-empty). On fresh entry from lobby/grid the centralized
  // subscribe effect above has just subscribed the tournament for the first
  // time; re-asserting here would double-emit `subscribeTournament` on the
  // wire (the reported "joining a tournament table subscribes twice" bug).
  const focusedGameIdForResub = focusedTableMatch?.params.gameID ?? '';
  const prevFocusedGameIdForResubRef = useRef<string>('');
  const resubscribeTournamentRef = useRef(gameService.resubscribeTournamentDetails);
  resubscribeTournamentRef.current = gameService.resubscribeTournamentDetails;
  useEffect(() => {
    const prevFocused = prevFocusedGameIdForResubRef.current;
    prevFocusedGameIdForResubRef.current = focusedGameIdForResub;
    if (!focusedGameIdForResub) return;
    // Fresh entry — the centralized subscribe effect just handled it. Bail so
    // we don't fire a redundant `subscribeTournament` alongside the one the
    // set-diff already emitted.
    if (!prevFocused) return;
    // Same table — the URL didn't actually navigate. Nothing to re-assert.
    if (prevFocused === focusedGameIdForResub) return;
    subscribedTournamentsRef.current.forEach((tid) => {
      // eslint-disable-next-line no-console
      console.log('[TNM-SUB] re-assert', tid, '| focused table now', focusedGameIdForResub);
      resubscribeTournamentRef.current?.(tid);
    });
  }, [focusedGameIdForResub]);

  // Not-logged-in visitors are NOT forced to the login screen — they land on
  // the guest lobby (see the `/*` route below). Login remains available
  // explicitly via the `/login` route.
  //
  // COLD-START SESSION RECOVERY lives in useBootstrapSessionRecovery (see its
  // docblock for the full rationale) — an expired session token is not an
  // ended session, and the refresh token must be spent before this visitor is
  // written off as a guest. It is a hook rather than an inline effect so the
  // mount-time COMPOSITION — the part that was wrong and tested nowhere — is
  // reachable from a test. Called HERE, at the position the inline effect
  // occupied, so its mount ordering relative to the scheduler tail below is
  // unchanged (the bootstrap refresh must stamp lastRefreshAt before
  // startTokenRefresh computes its floor).
  const { showSplash, hideSplash } = useContext(SplashScreenContext);
  useBootstrapSessionRecovery({ showSplash, hideSplash });

  const isGuest = useGuest();
  const lobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);

  // Auto-reload the instant a MEMBER session becomes a guest (token expired
  // mid-session) — reusing the exact same isGuest detection the "Login or
  // Sign up to join" CTAs (JoinButton.tsx) already rely on. isGuest can flip
  // true here from nothing more than wall-clock time passing the JWT's exp
  // (hasValidAuthToken() is a pure time check — no clearAuth()/network call
  // required to trigger the next re-render's recompute), so clearAuth() is
  // called here too — otherwise the stale playerGUID/subscribedTables keys in
  // localStorage would survive the reload (unlike Redux state, localStorage
  // persists across a real page load), reopening the exact identity-retention
  // bug this effect exists to close. Idempotent if clearAuth() already ran
  // elsewhere first (e.g. waitlistService's handleAuthFailure) — a second
  // call is a harmless no-op.
  //
  // Deliberately an EDGE (was member, now guest), not a level check ("is
  // guest") — isGuest is ALSO true for an ordinary visitor who never logged
  // in, and reloading on every guest page load would loop forever (see
  // ConnectionManager's isGuestSession() guard for the same class of bug this
  // avoids). Ref starts at the current value so mounting as a guest never
  // fires this on its own.
  const wasMemberRef = useRef(!isGuest);
  useEffect(() => {
    if (isSessionEndTransition(wasMemberRef.current, isGuest)) {
      // Field-diagnosability: mid-game logouts previously nuked the SPA with
      // zero breadcrumb. Log the exp delta so triage can tell "the token
      // really did expire" from "something else flipped isGuest" — the RCA's
      // top-5 hypotheses split cleanly on this one datum.
      const exp = readAccessTokenExp();
      const expDeltaSec = exp === null ? 'NO_TOKEN' : Math.round((exp - Date.now()) / 1000);
      console.warn('[Auth] session-end transition — clearing + hardReload', {
        wasMember: wasMemberRef.current,
        isGuest,
        expDeltaSec
      });
      clearAuth();
      hardReload();
    }
    wasMemberRef.current = !isGuest;
    // Boot / maintain the refresh scheduler alongside the member flag. Every
    // storeAuthTokens() call also self-schedules, so this covers the ONE gap
    // storeAuthTokens can't reach on its own: a fresh page load where the
    // session already lives in the cookie and nothing has re-stored it yet.
    //
    // `canAttemptRefresh()` is part of the condition because `isGuest` is also
    // true for an expired-but-refreshable session, and stopping the scheduler
    // there is what left a cold start with no way back. startTokenRefresh()
    // already handles a past-exp token by firing immediately (pinned by
    // authTokenRefresh.test.ts's 'fires immediately when the token is already
    // past exp'), so the capability was always there — only this gate withheld
    // it. Genuine guests still hit stopTokenRefresh(): canAttemptRefresh()
    // returns false with no refresh token, an expired one, or a dead family.
    if (!isGuest || canAttemptRefresh()) startTokenRefresh();
    else stopTokenRefresh();
  }, [isGuest]);

  // Watchdog re-sync — the scheduler that startTokenRefresh sets up fires
  // against the cookie's exp AT SCHEDULE TIME. If the cookie is later
  // rewritten with an earlier exp (multi-tab race, external mutation, or a
  // login handoff we didn't hear about), the stale timer would fire far too
  // late. A cheap ~30s poll re-anchors the schedule to the current cookie —
  // a no-op re-schedule in the normal case, and a fast catch-up in the edge
  // case. 30s < REFRESH_MARGIN_MS so the catch-up always beats the margin.
  useEffect(() => {
    if (isGuest) return;
    const interval = setInterval(() => {
      startTokenRefresh();
    }, 30_000);
    return () => clearInterval(interval);
  }, [isGuest]);

  // Foreground catch-up — mobile OSes (and browsers) can throttle or pause
  // setInterval / setTimeout while the tab is hidden or the app is
  // backgrounded. When the user comes back, we can't wait up to 30s for the
  // watchdog to tick — the very next socket message or fetch would go out
  // with a possibly-stale token. Re-anchor the schedule the instant the
  // page becomes visible, and again on Capacitor's stronger native
  // appStateChange signal (WKWebView / Android WebView don't always fire
  // document visibilitychange at the same moment the app resumes). The
  // MIN_REFRESH_INTERVAL_MS floor in startTokenRefresh keeps this from
  // hammering Descope even if the user rapidly cycles tabs.
  useEffect(() => {
    if (isGuest) return;
    const kick = () => {
      if (document.visibilityState === 'visible') startTokenRefresh();
    };
    document.addEventListener('visibilitychange', kick);
    // Capacitor native — fires on iOS/Android app lifecycle changes, which
    // don't always mirror document visibilitychange. `isActive: true` is
    // the foreground signal.
    let removeNativeListener: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', (state: { isActive: boolean }) => {
        if (state.isActive) startTokenRefresh();
      })
        .then((handle) => {
          removeNativeListener = () => handle.remove();
        })
        .catch(() => {
          /* noop */
        });
    }
    return () => {
      document.removeEventListener('visibilitychange', kick);
      removeNativeListener?.();
    };
  }, [isGuest]);
  const {
    closePanel: closeSupport,
    panelOpen: supportOpen,
    handleIncomingMessage: handleSupportMessage
  } = useSupportContext();

  useWaitlistSocketSync(socket);
  // Keep every table the player is waitlisted on open as a grid cell / tab —
  // joining a waitlist (lobby or table) and page refreshes otherwise leave the
  // queued table with no visible cell (see the hook doc).
  useOpenWaitlistedTables(socket);
  usePlayerMoneySocketSync(socket);
  useColluderRestrictionsSync(socket);
  // EN-11151: the streak snapshot (REST read, then game-ws pushes). Behind ops.streaks.
  useStreakSync(socket);
  // /demo-table renders below without ever leaving the 'lobby' view, so it
  // must not count as the lobby, or every demo moment loses beat two.
  useDropMomentInLobby(activeView === 'lobby' && window.location.pathname !== '/demo-table');
  // Handle the native login deep-link callback app-wide, so a guest can launch
  // login directly from a CTA (redirectToLogin opens the system browser) without
  // routing through LoginScreen. maintenanceInfo mirrors LoginScreen's web-flow
  // gate — the native callback applies the same game-close maintenance check
  // before entering the app (see the hook's comments).
  const {
    loginInProgress,
    loginFailure: nativeLoginFailure,
    clearLoginFailure: clearNativeLoginFailure,
    maintenanceInfo: nativeLoginMaintenanceInfo,
    clearMaintenanceInfo: clearNativeLoginMaintenanceInfo
  } = useNativeLoginDeepLink();
  const biometricOffer = useBiometricEnrollmentOffer();
  // The enrollment sheet or its confirmation beat is on screen. The multi-run
  // introduction and its Lobby Lab preview both wait on exactly this (EN-10791).
  const biometricPromptOpen =
    biometricOffer.show || biometricOffer.busy || biometricOffer.enrolled || biometricOffer.failed;

  // Native login: cover the guest lobby with the shared splash until the app
  // hard-reloads into the member session, so the login → member handoff looks
  // continuous (no guest-lobby flash). Routed through SplashScreenContext (see
  // its docblock) instead of rendering <SplashScreen> directly, so this and
  // Lobby's own "not ready yet" splash share one mount point.
  // (showSplash/hideSplash are destructured once, above, next to the
  // useBootstrapSessionRecovery call that also needs them — the cold-start
  // cover lives in that hook now.)
  useEffect(() => {
    if (!loginInProgress) return undefined;
    showSplash('nativeLogin');
    return () => hideSplash('nativeLogin');
  }, [loginInProgress, showSplash, hideSplash]);

  // Once the member lobby has actually loaded (lobbyReady — Lobby's own
  // 'lobbyLoading' splash reason already covers everything before this),
  // consume whatever return path redirectToLogin() stashed and navigate there
  // client-side. Unconditional — NOT gated on isRestrictedFlag/geolocation
  // the way Lobby's own geoFindMe fallback is, so this actually fires for
  // every member, not just restricted ones. consumeLoginReturnPath() no-ops
  // (returns the current path) when nothing was stashed, so the common case
  // (an ordinary login with no pending return path) never shows this splash
  // or navigates anywhere. returnPathHandledRef ensures this can only ever
  // fire once per app boot — consumeLoginReturnPath() is destructive
  // (self-clearing), so a second run would just see nothing left to consume.
  const returnPathHandledRef = useRef(false);
  useEffect(() => {
    if (isGuest || !lobbyReady || returnPathHandledRef.current) return;
    returnPathHandledRef.current = true;

    const path = consumeLoginReturnPath();
    const currentPath = window.location.pathname + window.location.search;
    if (path === currentPath) return;

    // A pending tournament-registration intent (markTournamentIntentBeforeLogin,
    // set by TournamentDetailModal's "Login/Sign up to Register") takes priority
    // over restoring the return path. That flow can be triggered from a table's
    // own tournament panel — not just the lobby — so the stashed return path
    // can be that table. SaloonLobbyContainer's auto-reopen of the Confirm
    // Registration modal only exists on the lobby route; navigating to the
    // table here would yank that modal away mid-flow before the guest ever
    // sees it through. Let registration finish on the lobby instead.
    if (peekTournamentIntent()) return;

    showSplash('pendingReturnPath');
    navigate(path, { replace: true });
    // Deferred, not immediate: navigate() only SCHEDULES the route change —
    // the destination (e.g. Game.tsx's 'gameTableLoading') doesn't mount and
    // register its own reason until the NEXT render pass. Hiding synchronously
    // here would drop isBlocked to false in between, unmounting the splash
    // (flashing the real lobby underneath) right before the destination's own
    // splash mounts fresh — a visible "shows the splash twice" double-intro.
    // setTimeout(…, 0) pushes this past that render + effect-flush cycle, so
    // by the time it runs the destination has already had the chance to show
    // its own reason first — one continuous cover, no gap, no replay.
    setTimeout(() => hideSplash('pendingReturnPath'), 0);
  }, [isGuest, lobbyReady, showSplash, hideSplash, navigate]);

  // Initialize OneSignal + AppsFlyer on app start (both native-only; no-ops on web)
  useEffect(() => {
    initOneSignal();
    initAppsFlyer();
  }, []);

  // AppsFlyer `first_hand_played` — fires once per install the first time the
  // logged-in player is dealt hole cards on a live table (their own seat's
  // card1 becomes non-empty). Mirrors the Unity client's HandPlayedEvent hook.
  const mySeatId = useAppSelector((state) => state.currentTable.currentPlayer.seatId);
  useEffect(() => {
    if (mySeatId > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const myCard1 = (game as any)?.[`p${mySeatId}card1`];
      if (myCard1) {
        logAppsFlyerEvent(AF_EVENT.FIRST_HAND, { once: true });
      }
    }
  }, [mySeatId, game]);

  // Clear stale recentlyExitedTables marks on app mount (page refresh).
  // These marks are session-bound: they're set by an in-session cleanup
  // (deferred Leave Table, wrong-tournament redirect) to suppress
  // auto-resub during the next ~30 seconds. After a page refresh, the
  // player's intent has reset and the server's playerGames is the
  // authoritative "which tables you have" signal — keeping marks across
  // refresh wedges legitimate tables out of state.tables on every reload.
  useEffect(() => {
    try {
      localStorage.removeItem('recentlyExitedTables');
    } catch {
      /* noop */
    }
  }, []);

  // Keep screen awake while app is open (native only, no-op on web)
  useEffect(() => {
    KeepAwake.keepAwake().catch(() => {});
    return () => {
      KeepAwake.allowSleep().catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    Sentry.setUser({ email: playerEmail });

    // Wait for connection before authenticating
    const handleConnect = () => {
      // Guests have no token — createPlayer()/getToken() would redirect to the
      // account login page, fighting the guest-lobby landing (infinite redirect
      // loop). Skip the entire authenticated bootstrap for guest sessions.
      if (isGuest) {
        return;
      }
      console.log('Socket connected, initiating authentication...');
      authService.createPlayer();
      const playerGUID = localStorage.getItem('playerGUID') || '';
      if (playerGUID) {
        socket.emit(GET_ACTIVE_SCROLLING_NOTIFICATIONS, {
          context: 'all',
          audience: 'all',
          playerGUID
        });
      }
    };

    // Check if already connected
    if (socket.connected) {
      handleConnect();
    } else {
      // Wait for connection
      socket.on('connect', handleConnect);
    }

    socket.on('connect_error', (error: any) => {
      Sentry.captureException(error);
    });

    socket.on('disconnect', (error: any) => {
      Sentry.captureException(error);
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('connect_error');
      socket.off('disconnect');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Stop reconnecting once the player is known to be blocked.
  //
  // $connect refuses them server-side, but a refused WebSocket handshake is
  // indistinguishable from a dropped network at the client: the browser
  // deliberately hides the HTTP status of a failed upgrade, so all the adapter
  // sees is a close (usually 1006, no reason) and it correctly retries -- up to
  // 50 times with backoff. One blocked player produced 36 refused $connect
  // calls in four minutes, each now costing a DynamoDB read, and inflating the
  // connect-side numbers that observe mode is supposed to measure.
  //
  // The client cannot infer the refusal from the transport, but it does not
  // need to: /location/verify already told it. disconnect() sets
  // intentionalDisconnect, which is what stops scheduleReconnect.
  //
  // Terminal until a reload, by design: with the resume re-check gone there is
  // no in-session path back to allowed, so a player whose situation changes
  // restarts the app or reloads the page.
  //
  // Safe for the blocking screen's affordances -- nothing in Cashier or Profile
  // consumes SocketContext, so the player keeps both.
  //
  // ONLY VALID ON THE VENDOR PATH. Under ops.client-geofence-path legacy or
  // legacy-shadow the server
  // is not refusing anything: `$connect` skips the geofence entirely and the
  // modal is raised client-side from `gotLocation` over a perfectly healthy
  // socket. Disconnecting there would kill the lobby feed behind the modal and
  // there would be no refusal to stop retrying against -- the reconnect storm
  // this exists to prevent cannot happen, because nothing is being refused.
  //
  // Keyed on the COMMITTED path, not the live flag: the check pins its provider
  // per login, so reading the flag here let this effect act on an implementation
  // that is not the one that ran. The bad case is a legacy modal already on
  // screen over a healthy socket when the flag flips to vendor -- this would
  // then disconnect and kill the lobby feed behind it. `null` means not yet
  // committed, so nothing has refused anyone and there is nothing to tear down.
  useEffect(() => {
    if (committedGeoLegacy !== false) return;
    if (!isLocationInvalid || !socket) return;
    socket.disconnect();
  }, [isLocationInvalid, socket, committedGeoLegacy]);

  useEffect(() => {
    if (!socket) return;

    // Per-session record of tables we've already emitted SubscribeToGameUpdates
    // for. The lambda subscription TTL is 24h (TTL.SUBSCRIPTION in
    // shared/lib/dynamoHelpers.js), so once subscribed there's no need to
    // re-emit — the engine fires GOT_PLAYER_GAMES on every player-game state
    // change and without this dedup we'd re-subscribe N tables per tick.
    // Resets when the socket reconnects (each effect run gets a fresh
    // binding), matching the connectionId lifecycle on the API Gateway side.
    const subscribedThisSession: Record<string, string> = {};

    // Coalesce GOT_TEXAS/GOT_OMAHA bursts into one React render per frame.
    // Engine can deliver 8+ table-state messages within ~500ms during active
    // hands; without batching each one re-renders the entire table tree.
    // Queue payloads, then in a single RAF callback dispatch them all —
    // React 18 auto-batches dispatches in the same task into one render.
    let tablePayloadQueue: any[] = [];
    let tableRafId: number | null = null;
    let flushDeferredSinceMs: number | null = null;
    // Background tables render at most every 2s - see BackgroundTableBuffer.
    const bgBuffer = new BackgroundTableBuffer();
    // Tables a gesture is about to reveal get their held snapshot flushed the
    // moment the drag starts, so the felt sliding in is already current.
    const gestureGids = (): Set<string> => {
      const st = getDragState();
      if (st.phase === 'idle') return new Set();
      const s = st.session;
      return new Set([s.prev, s.cur, s.next].filter(Boolean) as string[]);
    };
    const flushTableUpdates = () => {
      // PERF: never process the batch while a table swipe/settle is in flight.
      // These dispatches are 26-71ms main-thread tasks on a live table, and the
      // swipe's drag offset + spring both run on the main thread - a flush
      // mid-gesture freezes the felt for 2-4 frames (measured: 12 such stalls
      // in one gesture window; investigation doc 8k). Payloads keep queueing
      // and the backlog is compacted to the newest snapshot per table below,
      // so landing costs at most one dispatch per open table. The 2s cap is a
      // safety net: if a gesture phase ever wedged non-idle, game state must
      // not freeze with it.
      const deferredMs =
        flushDeferredSinceMs === null ? 0 : performance.now() - flushDeferredSinceMs;
      if (getDragState().phase !== 'idle' && deferredMs < 2000) {
        if (flushDeferredSinceMs === null) flushDeferredSinceMs = performance.now();
        tableRafId = requestAnimationFrame(flushTableUpdates);
        return;
      }
      flushDeferredSinceMs = null;
      const rawFrames = tablePayloadQueue;
      tablePayloadQueue = [];
      tableRafId = null;
      // Decompression happens HERE, not in the socket handler: a large snapshot
      // costs 10-30ms to inflate+parse, and the handler runs whenever the
      // message lands - including mid-swipe (measured as ~32ms gesture stalls,
      // investigation doc 8k). At flush time the gesture deferral above has
      // already guaranteed the main thread is not mid-gesture.
      const decompressed: any[] = [];
      for (const raw of rawFrames) {
        try {
          decompressed.push(decompressData(raw));
        } catch {
          // A malformed frame must not poison the batch - skip it.
        }
      }
      // EN-11178: frames that arrived older than one already seen for their
      // table. Counted always; dropped behind release.table-frame-order;
      // reported to Sentry behind ops.frame-order-report. In ARRIVAL order,
      // before compaction: a late frame is caught whether or not compaction
      // would keep it, and every frame that survives is newer than all before
      // it, so compaction and the background buffer keep the newest.
      const reportFrameOrder = frameOrderReportRef.current;
      const admitted = admitTableFrames(decompressed, {
        dropStale: tableFrameOrderRef.current,
        onOutOfOrder: reportFrameOrder
          ? (frame) => reportOutOfOrderFrame(frame, playerGUIDRef.current)
          : undefined,
        onValveOpened: reportFrameOrder
          ? (valve) => reportFrameOrderValve(valve, playerGUIDRef.current)
          : undefined
      });
      const queue = admitted.length > 1 ? compactTablePayloads(admitted) : admitted;
      for (const payload of queue) {
        // The shutdown API deletes this table's server-side waitlist as soon as
        // the flag lands. Clear the matching local projection on the same table
        // frame so a dead WAIT preview cannot linger indefinitely (the removed
        // player receives no later waitlist broadcast once their DB row is gone).
        // Waitlisted tables are opened as observer subscriptions; close that
        // observer cell too. A genuinely seated player keeps access so the live
        // hand can finish without interruption.
        const pendingGameID = String(payload?.game?.gameID ?? '');
        const pendingCashTable = getPendingCashTableUpdate(
          payload,
          tablesRef.current?.[pendingGameID]
        );
        if (pendingCashTable) {
          const { gameID: shutdownGameID, gameType: pendingGameType, isSeated } = pendingCashTable;
          dispatch(clearWaitlistForGame({ gameId: Number(shutdownGameID) }));
          if (!isSeated) {
            markRecentlyExitedTable(shutdownGameID);
            socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
              gameID: shutdownGameID,
              game: pendingGameType
            });
            dispatch(removeTable(shutdownGameID));
            dispatch(
              updatePlayerGames(
                (playerGamesRef.current || []).filter(
                  (entry: any) => entry && String(entry.gameID) !== shutdownGameID
                )
              )
            );
            if (shutdownGameID === focusedGameIdRef.current) {
              navigate('/', { replace: true });
            }
            continue;
          }
        }

        // Graceful cash-table shutdown publishes one terminal table frame with
        // closed=1 after all stacks are credited and seats are cleared in MySQL.
        // Treat it as a command, not another felt render. Previously this frame
        // was dispatched like an ordinary update, so the player remained on the
        // closed felt indefinitely even though the server had finished closing
        // it. It could also be resurrected by GOT_PLAYER_GAMES while the detach
        // update propagated.
        const closedCashTable = getClosedCashTableUpdate(payload);
        if (closedCashTable) {
          const { gameID: closedGameID, gameType: closedGameType } = closedCashTable;
          markRecentlyExitedTable(closedGameID);
          socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
            gameID: closedGameID,
            game: closedGameType
          });
          dispatch(removeTable(closedGameID));
          // The terminal table frame is authoritative: the server has already
          // credited stacks, detached seats, and marked this table closed.
          // Remove the stale lobby snapshot locally as well. This keeps the
          // card from lingering/reappearing if the separate LOBBY_UPDATE event
          // is delayed or missed by this client.
          dispatch(
            removeClosedCashGame({
              gameID: closedGameID,
              gameType: closedGameType as 'texas' | 'omaha'
            })
          );
          dispatch(
            updatePlayerGames(
              (playerGamesRef.current || []).filter(
                (entry: any) => entry && String(entry.gameID) !== closedGameID
              )
            )
          );
          if (closedGameID === focusedGameIdRef.current) {
            navigate('/', { replace: true });
          }
          continue;
        }

        // Re-check the exited guard at flush time. A payload may have
        // passed queueTableUpdate's check, then a leaveTable / SNG
        // unregister cleanup ran in the same task before RAF fired and
        // marked the gid as recently-exited. Dispatching here would
        // recreate state.tables[gid] (via the tables-slice extraReducer
        // routing on meta.gameID) right after removeTable cleared it,
        // making the TableHeader chip flicker back on screen.
        const gid = String(payload?.game?.gameID);
        // A table AUTO-CLOSED because the player is seated elsewhere in the
        // tournament must never resurrect for the session — the engine keeps it
        // in playerGames so a late/ re-subscribed GOT_TEXAS would otherwise pop
        // it back (stuck after busting again). Unconditional: it can't be the
        // balancing destination (that's a table they're moving TO, not away from).
        if (gid && isReconciledAwayTable(gid)) continue;
        // …but NEVER drop the active balancing destination — it may be inside the
        // recently-exited grace from a prior move, yet its broadcast is exactly
        // the one that hydrates the balanced-to cell / clears the loading overlay.
        if (gid && isRecentlyExitedTable(gid) && !isProtectedTable(gid)) continue;
        // Background-table throttle: an unfocused, un-gestured table's payload
        // parks in its holding cell (newest snapshot, increments merged) and
        // renders at most every BACKGROUND_TABLE_DISPATCH_MS - each background
        // dispatch renders a whole hidden keep-alive subtree (the 55-105ms
        // ambient tasks of investigation doc 8j/8q).
        const focused = gid === focusedGameIdRef.current;
        if (gid && gid !== '0' && !focused && !gestureGids().has(gid)) {
          const now = performance.now();
          if (!bgBuffer.isDue(gid, now)) {
            bgBuffer.hold(gid, payload);
            continue;
          }
          const held = bgBuffer.take(gid);
          const merged = held ? compactTablePayloads([held, payload])[0] : payload;
          bgBuffer.markDispatched(gid, now);
          dispatch({
            ...updateCurrentTable(merged),
            meta: { gameID: gid, playerGUID: playerGUIDRef.current }
          });
          continue;
        }
        // Only the on-screen table updates the shared currentTable slice. Other
        // open tables would otherwise rewrite it every frame and re-render every
        // currentTable subscriber (chiefly the App root) 1×-per-table-per-frame.
        if (gid && focused) {
          // Focused table: fold in anything held from before it was focused.
          const held = bgBuffer.take(gid);
          const merged = held ? compactTablePayloads([held, payload])[0] : payload;
          dispatch(updateCurrentTable(merged));
          if (gid !== '0') {
            dispatch({
              ...updateCurrentTable(merged),
              meta: { gameID: gid, playerGUID: playerGUIDRef.current }
            });
          }
          continue;
        }
        if (gid && gid !== '0') {
          // Gesture-revealed table: dispatch live while it is (about to be) on
          // screen, folding in any held snapshot.
          const held = bgBuffer.take(gid);
          const merged = held ? compactTablePayloads([held, payload])[0] : payload;
          bgBuffer.markDispatched(gid, performance.now());
          dispatch({
            ...updateCurrentTable(merged),
            meta: { gameID: gid, playerGUID: playerGUIDRef.current }
          });
        }
      }
      // A gesture starting is also when held snapshots for its tables must
      // land even if no fresh payload is in this batch - the drag-phase
      // deferral above resumes flushing the moment the gesture ends, and the
      // pre-warm/live path handles steady state, so simply draining any held
      // snapshots for currently-gestured tables here keeps reveals current.
      gestureGids().forEach((gid) => {
        const held = bgBuffer.take(gid);
        if (held) {
          bgBuffer.markDispatched(gid, performance.now());
          dispatch({
            ...updateCurrentTable(held),
            meta: { gameID: gid, playerGUID: playerGUIDRef.current }
          });
        }
      });
    };
    const queueTableUpdate = (rawFrame: any) => {
      // The queue now holds RAW (still-compressed) frames - gameID is unknown
      // until flush-time decompression, so the exited/reconciled-away guards
      // that used to run here run only in flushTableUpdates, which always
      // re-checked them anyway (the queue-vs-mark race made that mandatory).
      tablePayloadQueue.push(rawFrame);
      // Backstop against a pathological backlog: requestAnimationFrame does NOT
      // fire while the tab/app is backgrounded, but socket messages keep queuing
      // the whole time it's hidden. On resume the single RAF flush would then
      // dispatch the entire accumulated backlog in one synchronous task and
      // freeze the UI. Cap the queue and shed the OLDEST (stalest) snapshots so
      // that flush stays bounded. Normal in-hand bursts are ~8–20 messages, well
      // under the cap, so live play is never trimmed — this only trims a large
      // accumulated backlog down to its most recent entries.
      const MAX_TABLE_QUEUE = 400;
      if (tablePayloadQueue.length > MAX_TABLE_QUEUE) {
        tablePayloadQueue.splice(0, tablePayloadQueue.length - MAX_TABLE_QUEUE);
      }
      if (tableRafId === null) {
        tableRafId = requestAnimationFrame(flushTableUpdates);
      }
    };

    socket.on(GOT_TEXAS, (data: any) => {
      if (isReplaying()) return; // drop live frames so they don't fight a replay
      recordFrame(GOT_TEXAS, data); // hand-replay recorder (no-op unless recording)
      queueTableUpdate(data); // raw - decompressed at flush (see flushTableUpdates)
    });

    socket.on(GOT_OMAHA, (data: any) => {
      if (isReplaying()) return; // drop live frames so they don't fight a replay
      recordFrame(GOT_OMAHA, data); // hand-replay recorder (no-op unless recording)
      queueTableUpdate(data); // raw - decompressed at flush (see flushTableUpdates)
    });

    socket.on(GOT_PLAYER_PROMOS, (data: any) => {
      const parsed = decompressData(data);
      dispatch(updatePlayerPromos(parsed?.data || parsed));
    });

    socket.on(GOT_LOCATION, (data: any) => {
      data = decompressData(data);
      // The LEGACY path's verdict, restored with the ops.client-geofence-path
      // fallback. Inert while the vendor path is selected: nothing emits
      // TRACK_LOCATION then, so this listener simply never fires.
      //
      // Delegates the decision to the pure `computeLocationEventUpdates` helper
      // so the exact-flags-per-branch behaviour is covered by unit tests in
      // utils/__tests__/locationCheck.test.ts (RCA defect 1 — success must
      // clear ALL three flags together to close the modal). Applying the
      // updates conditionally so an `undefined` field means "leave the setter
      // untouched" — matches production semantics where an `error` status
      // leaves invalidLocationMsg intact for geoFindMe's per-code message.
      // The engine's own words for WHY, which nothing else records. It logs
      // none of this server-side, and `computeLocationEventUpdates` throws the
      // description away, so without this line a legacy verdict is unreadable
      // after the fact. The three it can return are worth telling apart:
      //
      //   "Location is valid."                   Split.io GeoFencing -> on
      //   "Location is invalid."                 Split.io GeoFencing -> off
      //   "No location restrictions configured." Split.io returned control,
      //                                          i.e. NO RULE EXISTS for this
      //                                          region, so the legacy path
      //                                          cannot refuse anyone here.
      //
      // That last one is indistinguishable from a pass at the UI, which is how
      // an environment can look like it is geofencing when it is not.
      console.log(
        `[geo] legacy verdict: status=${data?.status ?? 'none'} description=${
          data?.description ?? 'none'
        }`
      );

      // The description is passed because it is the ONLY thing carrying why.
      // Without it a region refusal and a failed lookup both rendered as the
      // modal's default copy, which asks the player to grant a location
      // permission -- useless advice to someone correctly refused for being
      // out of region, and actively misleading when the permission is already
      // granted.
      const updates = computeLocationEventUpdates(data?.status, data?.description);
      if (updates.isLocated !== undefined) setIsLocated(updates.isLocated);
      if (updates.isLocationInvalid !== undefined) setIsLocationInvalid(updates.isLocationInvalid);
      if (updates.invalidLocationMsg !== undefined)
        setInvalidLocationMsg(updates.invalidLocationMsg);
    });

    socket.on(GOT_SCROLLING_NOTIFICATIONS, (data: any) => {
      const notifications = getScrollingNotificationsFromPayload(data);
      if (notifications) {
        dispatch(updateScrollingNotifications(notifications));
      }
    });

    socket.on(GOT_DEVICE_CONNECTED, (data: any) => {
      data = decompressData(data);
      document.cookie = `_socketuuid=${data}`;
      setDeviceId(data);
    });

    socket.on(GOT_DUPLICATE_ACCESS, (data: any) => {
      data = decompressData(data);
      setDuplicateAccessMsg(data.msg);
      setIsDuplicateAccess(data.isDuplicate);
      // Engine now ships the gameID + game type of the table that triggered
      // the duplicate-access kick. The modal uses these to remove only that
      // specific table from the watcher list in grid view, rather than
      // tearing down every open table on the connection.
      setDuplicateAccessGameID(
        data.gameID !== null && data.gameID !== undefined ? String(data.gameID) : ''
      );
      setDuplicateAccessGame(data.game ?? '');
    });

    socket.on(GOT_LOGIN, (data: any) => {
      data = decompressData(data);
      if (
        data?.response?.error?.startsWith('Account not found') ||
        data?.response?.playerName === 'NOUSER'
      ) {
        clearAuthAndLogout();
      } else {
        dispatch(updateUser(data));
        dispatch(updateCurrentPlayerGUID(data?.response?.playerGUID));

        localStorage.setItem('playerGUID', data?.response?.playerGUID);
        localStorage.setItem('playerName', data?.response?.playerName);
        localStorage.setItem('deviceID', data?.response?.deviceID);

        authService.online(data?.response?.playerGUID);
        setIsLogged(true);

        // OneSignal: login + register channels + store device
        loginOneSignal(
          data?.response?.playerGUID,
          data?.response?.playerEmail,
          data?.response?.phone
        );
        storeOneSignalDevice(socket, data?.response?.playerGUID);

        // Request player's active games and data immediately after auth
        socket.emit(REGISTER_SOCKET, { page: 'lobby' });
        socket.emit(GET_PLAYER_PROMOS, { playerGUID: data?.response?.playerGUID });
        socket.emit(GET_ACTIVE_SCROLLING_NOTIFICATIONS, {
          context: 'all',
          audience: 'all',
          playerGUID: data?.response?.playerGUID
        });
      }
    });

    socket.on(GOT_JOIN_GAME, (data: any) => {
      data = decompressData(data);
      // EN-10064: a colluder hard-block (INTEGRITY_RESTRICTION) routes to the
      // dedicated integrity modal (generic copy + optional waitlist CTA), NOT
      // the generic "seat taken" error path. Detect it before the error branch.
      if (isIntegrityBlock(data?.response)) {
        dispatch(showIntegrityBlock(data.response));
        return;
      }
      if (data?.response?.error) {
        // A seat rejection belongs to ONE table. Route it onto that table's
        // slice (meta.gameID) so SeatJoinErrorModal renders it inside that
        // table's cell with player-facing copy — not as an app-wide modal that
        // blurs every open table and quotes the engine's log text. The engine's
        // "Seat taken!" lock frame carries no tableId, so fall back to the
        // attempt sitDown remembered (services/pendingSeatJoin), which retires
        // that attempt as it answers: the engine has now responded to it, and a
        // dead attempt left pending makes the NEXT table's silent rejection
        // ambiguous and pushes it back onto the app-wide modal.
        const target = resolveAndRetireSeatJoinError(data);
        if (target) {
          dispatch({
            ...setSeatJoinError({
              message: String(data.response.error),
              seatId: target.seatId,
              at: Date.now()
            }),
            meta: { gameID: target.gameID }
          });
          return;
        }
        // No table can be named for this rejection — keep the app-level modal
        // rather than swallow it. Titled "Buy-in": informational, not a system
        // error.
        dispatch(
          updateErrorMsg({
            msg: data?.response?.error,
            title: 'Buy-in',
            open: true
          })
        );
      } else {
        // Answered without an error: this join is DONE (the engine sends
        // `error: ''` on success — apps/engine/texas/socket.js:2369). Retire the
        // remembered attempt, or a completed join stays the last one standing
        // and answers for a later silent rejection that was never its own.
        clearPendingSeatJoin(data?.tableId);
      }
    });

    socket.on(CREATE_PLAYER_RESPONSE, (data: any) => {
      data = decompressData(data);
      authService.loginPlayer(data);
      // AppsFlyer registration event (mirrors Unity's af_complete_registration
      // on NewPlayerEvent). createRsp is the response to creating a new player.
      logAppsFlyerEvent(AF_EVENT.COMPLETE_REGISTRATION);
    });

    socket.on(GOT_PLAYER_GAMES, (games: any) => {
      games = decompressData(games);
      console.log('[App] GOT_PLAYER_GAMES received:', games);
      dispatch(updatePlayerGames(games));

      // Determine which tables the player is seated at
      const activeGames = (games || []).filter(
        (g: any) => g && g.gameID && Object.keys(g).length > 1
      );
      const activeGameMap = new Map<string, string>(
        activeGames.map((g: any) => [String(g.gameID), g.game || 'texas'])
      );

      // Read & prune the recently-exited tables (set by GameService.leaveTable).
      // Tables remain in this list for a short grace window so a deferred-leave
      // engine broadcast doesn't immediately re-subscribe us before the leave
      // completes. After the grace window, normal auto-resub resumes.
      // Reconcile the exit marks against what the engine actually reports: a
      // mark is cleared when its table DISAPPEARS from this payload (the leave
      // landed), not when a timer runs out. The old fixed 30s window is what
      // let an exited table come back — it stopped suppressing on a clock,
      // whether or not the leave had taken effect.
      // The player's own exits (Exit Table) are reconciled the same way, and a
      // table they exited stays skipped while the engine still lists it.
      const exitedSet = reconcileExitsWithPlayerGames(activeGameMap.keys());

      // Identify "wrong tournament tables" — entries that belong to a
      // tournament where the player has status 'P' on a different gameID.
      // These slipped past per-table cleanup (the engine doesn't drop the
      // player from playerGames on UNSUBSCRIBE alone for observers), so
      // App.tsx's auto-resub keeps re-adding them. We clean them up here:
      // mark recently-exited (blocks future resubs + drops in-flight
      // gotTexas/gotOmaha), emit UNSUBSCRIBE so the API Gateway slot is
      // released, dispatch removeTable so Redux state reflects the cleanup,
      // and add the gid to a local exclusion set so the resub loop below
      // skips it this round.
      const wrongTournamentGameIDs = new Set<string>();
      const ptd = playerTournamentDetailsRef.current || [];
      for (const g of activeGames) {
        const gid = String(g.gameID);
        const tableState = tablesRef.current?.[gid];
        // Hard invariant: never clean up a table the player is seated at.
        // The wrong-tournament heuristic targets OBSERVED tables only; a
        // seated table is one they explicitly joined. Skipping here keeps
        // cash + tournament seats safe even if a transient state in the
        // Redux store would otherwise look like a sibling match.
        if (tableState?.currentPlayer?.isSeated === true) continue;
        const tournId = tableState?.game?.tournamentId;
        if (!tournId) continue;
        // Primary: lobby data. Fallback: seat heuristic (works when the
        // player reloaded directly into a table and Lobby never mounted).
        const target =
          computeActiveTournamentRedirect(tournId, gid, ptd) ??
          computeActiveTournamentRedirectFromSeat(tournId, gid, tablesRef.current);
        if (target === null) continue;
        wrongTournamentGameIDs.add(gid);
      }
      wrongTournamentGameIDs.forEach((gid) => {
        // Same holds as the details-feed sweep (shouldHoldTableThroughRebalance).
        // This sweep is the one that used to lack them: it runs off a
        // different feed from the gotTnmStatus that starts the balancing flow,
        // so it could remove the SOURCE of a rebalance before
        // TournamentScreenModal even detected the move — no grid rebind, no
        // slot protection — and a two-table grid then read one table and
        // handed off to single view of the other one (EN-10625). Also covers
        // the final-table reshuffle: the engine transiently reports the player
        // "active elsewhere" while the OLD cell is still transitioning.
        if (
          holdsTableThroughRebalance(
            gid,
            ptd,
            tablesRef.current,
            recentAssignedSeatsRef.current,
            closedGameIdsRef.current
          )
        ) {
          console.log('[App] Holding wrong-tournament cleanup through a rebalance:', gid);
          return;
        }
        console.log('[App] Cleaning up wrong tournament table:', gid);
        const gameTypeForTable = tablesRef.current?.[gid]?.game?.game || 'texas';
        // Session-durable mark (same as the fast-feed reconcile): whichever event
        // sees the player active-elsewhere first pins it, so the auto-resub below
        // and a late GOT_TEXAS can't resurrect it — no close/open flicker in grid.
        markReconciledAwayTable(gid);
        markRecentlyExitedTable(gid);
        socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
          gameID: gid,
          game: gameTypeForTable
        });
        dispatch(removeTable(gid));
      });

      // The previous map is { gameID: gameType } so we can emit unsubscribe
      // with the correct game type (lambda's unsubscribeGame defaults to
      // texas, which would silently no-op for omaha tables).
      let prevSubscribed: Record<string, string> = {};
      try {
        const raw = JSON.parse(localStorage.getItem('subscribedTables') || '{}');
        if (Array.isArray(raw)) {
          // Migrate legacy array shape — we don't know the gameType, so
          // leave it blank and let the lambda default kick in.
          raw.forEach((id: any) => {
            prevSubscribed[String(id)] = '';
          });
        } else if (raw && typeof raw === 'object') {
          prevSubscribed = raw;
        }
      } catch {
        /* noop */
      }

      // Unsubscribe from tables we're no longer seated at — single emit;
      // UNSUBCRIBE_TO_GAME_UPDATES === 'UnsubscribeFromGameUpdates' and
      // ConnectionManager already routes that string to the API Gateway
      // WebSocket. Emitting both used to fire the lambda twice and race on
      // the connection's DynamoDB item.
      Object.entries(prevSubscribed).forEach(([prevID, prevGame]) => {
        if (!activeGameMap.has(prevID)) {
          console.log('[App] Unsubscribing from table no longer seated:', prevID);
          socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, { gameID: prevID, game: prevGame });
        }
      });

      // Subscribe to each active (seated) table — except ones the user has
      // just explicitly exited (deferred leave grace window), wrong
      // tournament tables we just cleaned up above, OR tables we've
      // already subscribed to in this socket session. The engine fires
      // GOT_PLAYER_GAMES on every player-game state change and the
      // subscription TTL is 24h, so re-emitting on each tick would just
      // produce identical lambda no-ops while spamming the network.
      const tablesToSubscribe = activeGames.filter((g: any) => {
        const gid = String(g.gameID);
        const game = g.game || 'texas';
        if (exitedSet.has(gid)) return false;
        if (wrongTournamentGameIDs.has(gid)) return false;
        // Auto-closed-away table (player seated elsewhere in the tournament) —
        // the engine keeps it in playerGames, but the client deliberately left
        // it; don't resurrect it via auto-resub (would come back after busting).
        if (isReconciledAwayTable(gid)) return false;
        // Already subscribed with the same game type in this session →
        // skip. A different game type for the same gid (rare — would mean
        // the engine reclassified a table) still re-subscribes.
        if (subscribedThisSession[gid] === game) return false;
        return true;
      });
      if (tablesToSubscribe.length > 0) {
        console.log(
          '[App] Auto-subscribing to active tables:',
          tablesToSubscribe.map((g: any) => g.gameID),
          exitedSet.size > 0 ? `(skipped: ${Array.from(exitedSet).join(',')})` : ''
        );
      }
      // Note: we deliberately do NOT emit REGISTER_SOCKET { page: 'game', gameID }
      // for every active table here. The engine treats that as "the player's
      // current game" (last write wins), so iterating over all subscribed
      // tables would clobber whichever table the URL is actually on and pull
      // the user back to a different table. The single-table Game view in
      // pages/Game.tsx owns the engine's current-game registration; the
      // auto-resub only needs to keep the API Gateway lambda subscriptions
      // alive so multi-table broadcasts continue to flow.
      tablesToSubscribe.forEach((g: any) => {
        const gid = String(g.gameID);
        const game = g.game || 'texas';
        socket.emit('SubscribeToGameUpdates', {
          gameID: gid,
          game,
          playerGUID: localStorage.getItem('playerGUID') || '',
          username: localStorage.getItem('playerName') || ''
        });
        subscribedThisSession[gid] = game;
      });

      // Drop the in-session record for any table no longer in activeGames —
      // either the player left it (handled by the unsubscribe diff above)
      // or it was cleaned up as a wrong-tournament observer. Without this
      // the dedup would silently block a legitimate re-subscribe if the
      // player rejoins the same table later in the same session.
      Object.keys(subscribedThisSession).forEach((gid) => {
        if (!activeGameMap.has(gid)) delete subscribedThisSession[gid];
      });

      // Track what we're subscribed to for next comparison. Only include
      // tables we actually (re)subscribed; entries we deliberately skipped
      // shouldn't poison the next round's diff.
      const nextSubscribed: Record<string, string> = {};
      tablesToSubscribe.forEach((g: any) => {
        nextSubscribed[String(g.gameID)] = g.game || 'texas';
      });
      try {
        localStorage.setItem('subscribedTables', JSON.stringify(nextSubscribed));
      } catch {
        /* noop */
      }
    });

    socket.on(GOT_LIVE_CHAT_NOTIFICATION, (liveChatNotifications: any) => {
      liveChatNotifications = decompressData(liveChatNotifications);
      dispatch(updateLiveChatNotifications(liveChatNotifications));
    });

    // socket.on(GOT_CHARGED, (data: any) => {
    // dispatch(updateCharged(data));

    // dispatch(
    //   addHistory([
    //     {
    //       msg: `${data.msg} - ${moment(data.time).local().format('hh:mm:ss A')}`,
    //       systemMsg: true,
    //       isShowCard: false,
    //       isCharge: true
    //     }
    //   ] as History[])
    // );

    // setTimeout(() => {
    //   dispatch(updateCharged(null));
    // }, 1000);
    // });

    // TO DO:
    // Turning this off, this is not doing anything as of the moment.
    // We can add this back once we have the online indicator / signal strength.
    // socket.on(GOT_HEARTBEAT, ( usr:any ) => {
    // console.log("GOT_HEARTBEAT", usr);
    // });

    socket.on(GOT_MAINTENANCE_SCHEDULE, (data: any) => {
      data = decompressData(data);
      dispatch(setMaintenanceSchedule(data));
    });

    socket.on(GOT_SERVER_TIME, (data: any) => {
      data = decompressData(data);
      dispatch(setServerDetails(data));
    });

    socket.on(GOT_REFRESH_TOKEN, (refreshToken: any) => {
      refreshToken = decompressData(refreshToken);
      if (refreshToken?.error === 'invalid_grant') {
        if (isMaintenanceRef.current) {
          console.log('[Auth] Refresh token invalid ignored — maintenance in progress');
          return;
        }
        // Guard: an upstream Descope hiccup (5xx, key rotation) shouldn't
        // nuke a client whose CURRENT access_token is still exp-valid. But
        // "exp still in the future" is a WEAKER check than what Descope
        // is telling us — a token revoked server-side (admin action,
        // security event) still has a valid exp. So instead of silently
        // ignoring, force an IMMEDIATE refresh to verify with Descope
        // (not startTokenRefresh, which would schedule minutes out). If
        // the token really is revoked → E064003/E064006 → terminal short-
        // circuit → session-end. If it was a transient engine hiccup, the
        // refresh succeeds and the session is preserved.
        //
        // `canAttemptRefresh()` widens the guard to the case it always should
        // have covered: an access_token that is genuinely PAST exp but backed
        // by a live refresh token. That is the one state a refresh exists for,
        // and testing only `hasValidAuthToken()` meant the handler verified
        // when there was least reason to and tore down when there was most.
        if (hasValidAuthToken() || canAttemptRefresh()) {
          console.warn(
            '[Auth] Refresh token invalid but session still refreshable — verifying with Descope now'
          );
          verifyRefreshNow();
          return;
        }
        console.log('[Auth] Refresh token invalid — clearing session');
        clearAuthAndLogout();
      } else {
        // Freshness guard: the client-side refresh scheduler now writes
        // tokens directly to this cookie via storeAuthTokens(). If the
        // engine emits a gotRefreshToken with an OLDER session token than
        // what the client already stored (engine's own refresh path is
        // slower / racing our scheduler), overwriting would send us into a
        // stale-token loop that Descope rejects with E064003. Only the
        // cookie WRITE is guarded — the socket.auth update + reconnect
        // must still run so the engine's next handshake sees the client's
        // fresh token, otherwise the engine keeps refreshing with its
        // stale cached copy and we're back in the same race.
        let incomingIsStale = false;
        try {
          const incomingExp = refreshToken?.access_token
            ? decodeJwtPayload(refreshToken.access_token)?.exp * 1000
            : 0;
          const currentAccess = getAccessToken();
          const currentExp = currentAccess ? decodeJwtPayload(currentAccess)?.exp * 1000 : 0;
          if (incomingExp && currentExp && incomingExp <= currentExp) {
            console.warn(
              '[Auth] Ignoring stale gotRefreshToken cookie write — client has newer token',
              { incomingExp, currentExp }
            );
            incomingIsStale = true;
          }
        } catch {
          /* fall through — if we can't compare, prefer the engine's write */
        }

        if (!incomingIsStale) {
          const tokenStr = JSON.stringify(refreshToken);
          Cookies.set(DESCOPE_COOKIE as string, tokenStr, { path: '/' });
          Cookies.set(DESCOPE_COOKIE as string, tokenStr as any, {
            path: '/',
            domain: `.${APP_DOMAIN}`
          });
          // Also update localStorage for native fallback
          try {
            localStorage.setItem(DESCOPE_COOKIE as string, tokenStr);
          } catch {
            /* noop */
          }
        }

        // Always re-auth the socket and reconnect. If the engine's token
        // was newer we just wrote it to the cookie; if it was stale we
        // still push our OWN fresh token onto socket.auth so the engine
        // reads the freshest one on its next handshake. Without this
        // reconnect the engine keeps whatever it had cached and races
        // client-side rotation again.
        const freshTokens = incomingIsStale ? getAuthToken() : refreshToken;
        socket.auth = {
          access_token: freshTokens.access_token,
          refresh_token: freshTokens.refresh_token
        };
        socket.connect();
      }
    });

    socket.on(GOT_TOURNAMENT_STATUS, (data: any) => {
      data = decompressData(data);
      // Legacy engine sends a full array; API Gateway sends one status object at a time
      if (Array.isArray(data)) {
        dispatch(updateTournamentStatus(data));
      } else if (data?.response && Array.isArray(data.response)) {
        dispatch(updateTournamentStatus(data.response));
      } else if (data && data.tournamentId) {
        // Single status from API Gateway — merge into existing array
        dispatch(mergeTournamentStatus(data));
      }

      // Closed-table sweep: gotTnmStatus carries closedGameIDs (the tournament's
      // broken/closed tables). Drop any table the player has OPEN that is now
      // closed so it disappears from "Your Tables" and can't be revisited after
      // they observe away from one (reported bug: the dead table lingered as a
      // grid cell / Your-Tables entry). EXCEPT the table they're actively viewing
      // in single-table view — removing that mid-render blanks the felt; the
      // in-table modal (X / Observe) handles that one. game.closed can't drive
      // this (a closed table stops sending gotTexas), but closedGameIDs can.
      const extractClosedIds = (d: any): string[] => {
        if (!d) return [];
        if (Array.isArray(d.closedGameIDs)) return d.closedGameIDs.map(String);
        const arr = Array.isArray(d) ? d : Array.isArray(d?.response) ? d.response : [];
        for (const rec of arr) {
          if (Array.isArray(rec?.closedGameIDs)) return rec.closedGameIDs.map(String);
        }
        return [];
      };
      const closedIds = new Set(extractClosedIds(data));
      // Publish to the App-wide ref so the gotPlayerTournamentDetails
      // reconciliation (a different socket handler on a different feed) can
      // consult the engine's authoritative closed list without racing the
      // build here.
      closedGameIdsRef.current = closedIds as Set<string>;
      if (closedIds.size > 0) {
        const focused = focusedGameIdRef.current;
        // Do NOT sweep a table that is any of:
        //   • the ACTIVE balancing DESTINATION (isProtectedTable) — the engine's
        //     cumulative closedGameIDs transiently lists it as closed mid-
        //     reshuffle, and in grid `focused` is '' so it isn't otherwise
        //     protected; sweeping it marks it recently-exited and drops its
        //     balancing-complete gotTexas → the cell is stuck loading forever;
        //   • a table the player is still SEATED at — a seated player whose
        //     table closes gets the in-table "table closed" prompt (Re-Entry /
        //     Observe / Exit), which owns that table's teardown;
        //   • an OPEN tournament cell the player is OBSERVING — a non-registered
        //     railbird whose observed table closes must ALSO get the closed-table
        //     prompt ([Observe Game / Exit Table]) in that cell (reported grid
        //     bug: the cell was removed instantly instead). isClosedTablePrompt
        //     doesn't gate on seat/registration, so keeping the cell (felt already
        //     emptied below) surfaces the prompt; the player's Observe/Exit owns
        //     teardown. Only Your-Tables GHOSTS (a closed gameID in playerGames
        //     with no open cell) are still swept, so nothing dead lingers there.
        const isSeatedTable = (gid: string) =>
          tablesRef.current?.[gid]?.currentPlayer?.isSeated === true;
        // The player's OWN participation table — they have a playerTournamentDetails
        // record for this gameID (seated OR busted: a busted player keeps their
        // record with gameID === the closed table). Keep it so the in-table
        // "table closed" prompt (Re-Entry / Observe / Exit) can render.
        const isOwnParticipationTable = (gid: string) =>
          (playerTournamentDetailsRef.current || []).some(
            (d: any) => String(d?.gameID) === String(gid)
          );
        // An OPEN tournament cell (MTT 'm' / SnG 't') — kept so its in-cell
        // closed-table prompt can render rather than the cell vanishing. Cash
        // ('s') cells never carry closedGameIDs, so this only ever matches
        // tournament tables.
        const isOpenTournamentCell = (gid: string) => {
          const tt = tablesRef.current?.[gid]?.game?.tabletype;
          return tt === 'm' || tt === 't';
        };
        // Guard against sweeping a cell that is in the middle of a final-
        // table reshuffle (game.isPostBalancing === 1). The engine
        // transiently lists tables in closedGameIDs during that reshuffle
        // while it moves players onto the final table; without this guard,
        // the grid cell disappears BEFORE the balancing-complete gotTexas
        // (isPostBalancing:0) can land — reported bug in multi-table view
        // (cell just closes during final balancing).
        const isPostBalancingTable = (gid: string) =>
          tablesRef.current?.[gid]?.game?.isPostBalancing === 1;

        // Wipe every seat on any OPEN table that is now closed (except the active
        // balancing destination). The engine has already transferred all players
        // off a closed table, but it stops sending gotTexas — so without this the
        // felt keeps showing the stale occupants. Clears the seats even on tables
        // we deliberately KEEP (the focused single-table felt, or a seated
        // player's grid cell showing the "table closed" prompt) so the felt
        // behind the prompt reads as emptied/transferred.
        closedIds.forEach((cid) => {
          const gid = String(cid);
          if (isProtectedTable(gid)) return;
          if (!tablesRef.current?.[gid]) return;
          dispatch({ ...clearTablePlayers(), meta: { gameID: gid } });
        });

        const isClosedAway = (gid: string) =>
          shouldSweepClosedTable({
            isClosed: closedIds.has(String(gid)),
            isFocused: String(gid) === focused,
            isProtected: isProtectedTable(gid),
            isSeated: isSeatedTable(gid),
            isOwnParticipation: isOwnParticipationTable(gid),
            // Keep an open tournament cell so its closed-table prompt shows; only
            // ghosts (no open cell → isOpenTournamentCell false) are swept.
            isOpenTournamentCell: isOpenTournamentCell(gid),
            // Never sweep a cell mid-final-table reshuffle — see docblock
            // on isPostBalancingTable above.
            isPostBalancing: isPostBalancingTable(gid)
          });
        const closedOpenTableGids = Object.keys(tablesRef.current || {}).filter(isClosedAway);
        const pg = playerGamesRef.current || [];
        const pgHasClosed = pg.some((g: any) => isClosedAway(String(g?.gameID)));
        if (closedOpenTableGids.length > 0 || pgHasClosed) {
          closedOpenTableGids.forEach((gid) => {
            const gtype = tablesRef.current?.[gid]?.game?.game || 'texas';
            // Grace-mark BEFORE unsubscribe so an in-flight broadcast can't
            // re-hydrate the table right after removeTable clears it.
            markRecentlyExitedTable(gid);
            socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, { gameID: gid, game: gtype });
            dispatch(removeTable(gid));
          });
          if (pgHasClosed) {
            dispatch(updatePlayerGames(pg.filter((g: any) => !isClosedAway(String(g?.gameID)))));
          }
        }
      }
    });

    // Keep the player's tournament details (assigned gameID / status /
    // shouldAutoRedirect) fresh in every view.
    // App owns this feed so it stays current in the lobby and at a table.
    // A second Lobby listener would dispatch each push twice and bypass the
    // reducer's empty-push debounce. Silence preserves last-known participation;
    // only server results establish that registrations are gone.
    socket.on(GOT_PLAYERTOURNAMENTDETAILS, (data: any) => {
      const details = decompressData(data?.data ?? data);
      if (details) {
        // Track this push's status:'P' seat assignments in the recent-seats
        // ref, then prune stale entries. This runs BEFORE dispatch so the
        // reconciliation below can consult a fully-current snapshot. See
        // recentAssignedSeatsRef's docblock for why this is a separate ref.
        const nowMs = Date.now();
        const RECENT_SEAT_GRACE_MS = 30_000;
        const list = Array.isArray(details) ? details : [];
        // A seat the engine assigns supersedes the player's earlier exit from
        // that table (re-entry / rebalance back onto it). Before this push's
        // seats are recorded below, so a NEW seat is told apart from a known one.
        releaseExitsForAssignedSeats(
          list,
          (tid, gid) => !!recentAssignedSeatsRef.current.get(tid)?.has(gid)
        );
        // Track this push's fresh 'P' assignments per tournament so we can
        // release stale-source guards below.
        const freshSeatsByTournament = new Map<number, Set<number>>();
        for (const d of list) {
          if (d?.status !== 'P') continue;
          if (d?.gameID === null || d?.gameID === undefined) continue;
          const tid = Number(d?.tournamentId);
          const gidNum = Number(d?.gameID);
          if (!Number.isFinite(tid) || !Number.isFinite(gidNum)) continue;
          let seatMap = recentAssignedSeatsRef.current.get(tid);
          if (!seatMap) {
            seatMap = new Map<number, number>();
            recentAssignedSeatsRef.current.set(tid, seatMap);
          }
          seatMap.set(gidNum, nowMs);
          let freshSet = freshSeatsByTournament.get(tid);
          if (!freshSet) {
            freshSet = new Set<number>();
            freshSeatsByTournament.set(tid, freshSet);
          }
          freshSet.add(gidNum);
        }
        // New-seat authoritative release: the engine has explicitly assigned
        // the player to `freshSet` for this tournament, so any OTHER gids we
        // were still tracking as "recently mine" (from a prior 'P' push on the
        // old seat, before bust / balancing move) are now stale. Drop them
        // from recentAssignedSeatsRef so the wasMySeat guard below stops
        // protecting them, AND clearProtectedTable() so the isProtectedTable
        // guard also releases — otherwise both guards keep the old MTT table
        // alive for their 30 s / 20 s TTLs, the destination openTable retries
        // in the meantime keep hitting the 4-table cap, and the player sees a
        // spurious "Reached Table Limit" modal on bust+re-entry and on
        // balancing-to-a-different-table. Only touches gids in the SAME
        // tournament as a fresh assignment — unrelated tournaments and cash
        // tables are untouched. Once released, the fast-feed cleanup below
        // removes the stale slice on the same push, freeing the cap slot
        // before the next openTable retry.
        for (const [tid, freshSet] of Array.from(freshSeatsByTournament)) {
          const seatMap = recentAssignedSeatsRef.current.get(tid);
          if (!seatMap) continue;
          for (const oldGid of Array.from(seatMap.keys())) {
            if (freshSet.has(oldGid)) continue;
            seatMap.delete(oldGid);
            clearProtectedTable(oldGid);
          }
          if (seatMap.size === 0) recentAssignedSeatsRef.current.delete(tid);
        }
        // Lazy prune.
        for (const [tid, seatMap] of Array.from(recentAssignedSeatsRef.current)) {
          for (const [gidNum, ts] of Array.from(seatMap)) {
            if (nowMs - ts > RECENT_SEAT_GRACE_MS) seatMap.delete(gidNum);
          }
          if (seatMap.size === 0) recentAssignedSeatsRef.current.delete(tid);
        }

        dispatch(updatePlayerTournamentDetails(details));
        // Reconcile open tables to the player's ACTUAL seat: once they're
        // actively playing a tournament (Re-Entry / late-register succeeded, or a
        // rebalance moved them), close any OTHER same-tournament tables still
        // open — the old seat or tables observed while busted — keeping only the
        // current seat's table. Otherwise they linger in the switcher / "Your
        // Tables" and hit "Reached Table Limit". Frees the engine slot too via
        // UNSUBSCRIBE. Covers single-table AND grid (operates on state.tables).
        // Clear the reconciled-away mark for every seat the player currently
        // holds, so a table they were auto-closed from before but are now
        // genuinely (re)seated at can hydrate again.
        (Array.isArray(details) ? details : []).forEach((d: any) => {
          if (d?.status === 'P' && d?.gameID !== undefined && d?.gameID !== null) {
            clearReconciledAwayTable(String(d.gameID));
          }
        });
        const staleGids = computeObservedTablesToClose(details, tablesRef.current);
        staleGids.forEach((gid) => {
          // Held while a rebalance is still moving the player off this table:
          // their recent seat (recentAssignedSeatsRef, a rolling window — this
          // feed overwrites playerTournamentDetailsRef, so a follow-up push of the
          // SAME balance would otherwise read "no change" and remove the source),
          // a protected balancing table (the fast feed can briefly still report the
          // OLD seat, which would close the destination), the source before its
          // destination has loaded (also the tournament-start case: the pre-play
          // observed table swept before the assigned seat subscribes), or a cell
          // mid final-table reshuffle. The engine closing the table overrides all
          // but the reshuffle — otherwise a failed balance (assigned to B, B closes,
          // reassigned to C) stranded the player on B for the 30 s seat window.
          if (
            holdsTableThroughRebalance(
              gid,
              details,
              tablesRef.current,
              recentAssignedSeatsRef.current,
              closedGameIdsRef.current
            )
          )
            return;
          // Process each stale table ONCE. This feed fires ~1/s and tablesRef
          // lags a render behind removeTable, so without this guard the same
          // table would be re-unsubscribed + re-removed every tick while the ref
          // catches up — churn that (in grid) fights the cell's mount re-subscribe
          // and makes the cell flicker closed/open/closed. The reconciled-away
          // mark IS the "already handled" flag.
          if (isReconciledAwayTable(gid)) return;
          const gtype = tablesRef.current?.[gid]?.game?.game || 'texas';
          // Session-durable mark so this table can't be resurrected by the
          // auto-resub / a late GOT_TEXAS after the 30s grace lapses or the
          // player busts again. Also grace-mark + unsubscribe + drop it now.
          markReconciledAwayTable(gid);
          markRecentlyExitedTable(gid);
          socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, { gameID: gid, game: gtype });
          dispatch(removeTable(gid));
        });
      }
    });

    // Global SNG-unregister cleanup. Lives here instead of in
    // TournamentDetailModal because (a) the unregister event has TWO
    // call-sites (TournamentDetailModal, TournamentScreenModal in-table) and
    // (b) the modal-scoped listener was torn down when the user closed the
    // modal before the engine response
    // arrived, leaving the chip dangling in state.tables forever.
    //
    // Engine reuses 'gotTnmUnRegister' for recoverable failures too — the
    // success/failure discriminator is data.response.title ('Unregister
    // Successful' vs 'Unregistration Failed'). MTT unregister has no
    // assigned table to clean (seat is allocated at start time, not at
    // register), so the cleanup is gated on the tournamentId appearing in
    // state.lobby.tournament.sitAndGoList.
    socket.on(GOT_TOURNAMENT_UNREGISTER, (raw: any) => {
      const data = decompressData(raw);
      if (!data) return;
      const title = data?.response?.title || '';
      if (title !== 'Unregister Successful') return;
      const tournamentId = data?.tournamentId;

      // The success response is authoritative: update the player's registration
      // snapshot immediately so every tournament UI flips back to "Register".
      // This is especially important for the final registration, because the
      // periodic details feed represents both "no rows" and a query failure with
      // the same -1 sentinel, which the reducer deliberately cannot use to clear
      // otherwise-valid state.
      dispatch(removePlayerTournamentDetail(tournamentId));

      const sngEntry = (sitAndGoListRef.current || []).find(
        (t: any) => Number(t?.tournamentId) === Number(tournamentId)
      );
      if (!sngEntry) return;
      const gameID = data?.gameID ?? sngEntry?.gameID;
      if (gameID === undefined || gameID === null || Number(gameID) <= 0) return;
      const gidStr = String(gameID);
      const game = data?.game || sngEntry?.game || 'texas';
      console.log('[App] SNG unregister cleanup', { gidStr, game, tournamentId });
      // Mark BEFORE emit — App.tsx's GOT_TEXAS / GOT_OMAHA queue (and its
      // flush-time re-check) drops any in-flight broadcast for this gid
      // during the 30s grace window, so the table can't be re-hydrated
      // immediately after removeTable clears it.
      markRecentlyExitedTable(gidStr);
      socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, { gameID: gidStr, game });
      dispatch(removeTable(gidStr));
      const filteredPg = (playerGamesRef.current || []).filter(
        (g: any) => g && String(g.gameID) !== gidStr
      );
      dispatch(updatePlayerGames(filteredPg));
    });

    socket.on(GOT_VERIFY_PRIVATE_TABLE_PASSWORD, (data: any) => {
      data = decompressData(data);
      if (data?.response?.isValid) {
        setOpenAskBuyin(true);
        setOpenAskPassword(false);
        setAskPasswordMsg('');
      } else {
        setAskPasswordMsg(data.response.msg);
      }
    });

    // Support chat — real-time agent reply notification
    socket.on(GOT_SUPPORT_MESSAGE, (data: any) => {
      const parsed = data?.data || data;
      if (parsed?.ticketId) {
        handleSupportMessage(parsed);
      }
    });

    // Any unregister answer, success or refusal, ends the Sit & Go waiting
    // panel's in-flight request, even when no panel is mounted to hear it.
    const unsubscribeUnregisterSettlement = subscribeSitGoUnregisterSettlement(socket);

    // Chrome 117+ blocks 'unload' via Permissions Policy because it breaks
    // back/forward-cache. 'pagehide' fires reliably on tab close, navigation,
    // and is bfcache-friendly. (The previous 'close' listener was a no-op —
    // 'close' is not a real window event.) The removal also needs a stable
    // handler reference so removeEventListener actually finds it.
    const handlePageHide = () => gameService.leaveTable(true);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      unsubscribeUnregisterSettlement();
      socket.off(GOT_TOURNAMENT_STATUS);
      socket.off(GOT_PLAYERTOURNAMENTDETAILS);
      socket.off(CREATE_PLAYER_RESPONSE);
      socket.off(GOT_LOGIN);
      socket.off(GOT_JOIN_GAME);
      socket.off(GOT_PLAYER_GAMES);
      socket.off(GOT_PLAYER_PROMOS);
      socket.off(GOT_LOCATION);
      socket.off(GOT_SCROLLING_NOTIFICATIONS);
      socket.off(GOT_TEXAS);
      socket.off(GOT_OMAHA);
      if (tableRafId !== null) cancelAnimationFrame(tableRafId);
      socket.off(GOT_MAINTENANCE_SCHEDULE);
      socket.off(GOT_SUPPORT_MESSAGE);
      socket.off(GOT_SERVER_TIME);
      socket.off(GOT_REFRESH_TOKEN);
      socket.off(GOT_DEVICE_CONNECTED);
      socket.off(GOT_DUPLICATE_ACCESS);
      socket.off(GOT_VERIFY_PRIVATE_TABLE_PASSWORD);

      window.removeEventListener('pagehide', handlePageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    // During maintenance, once the current cash hand ends, leave the table and
    // send the player back to the lobby (Unity parity). Gated on being on a
    // /table route so a stale currentTable in the lobby can't trigger it.
    // Previously this called window.close(), which only works for
    // script-opened popups — in a normal tab it silently failed, stranding the
    // player in a dead table view with no Join button.
    const onTableRoute = window.location.pathname.startsWith('/table');
    if (
      onTableRoute &&
      isMaintenance &&
      (game?.hand === '' || game?.hand === '0') &&
      game?.tabletype === 's'
    ) {
      gameService.leaveTable();
      setTimeout(() => navigate('/', { replace: true }), 1000);
    }
  }, [gameService, game?.hand, isMaintenance, navigate]);

  // Standalone seat/bet layout preview — bypasses auth/sockets/lobby so the
  // table can be eyeballed without a live session. Dev/QA only.
  if (window.location.pathname === '/demo-table') {
    return <DemoTable />;
  }

  return (
    <>
      <CssBaseline />
      <PerfHud />
      <ConnectionWarning />
      {/* Native login blocked by an active game-close maintenance window (see
          useNativeLoginDeepLink) — same notice LoginScreen's web flow shows. */}
      {nativeLoginMaintenanceInfo && (
        <MaintenanceNotice
          info={nativeLoginMaintenanceInfo}
          onBack={clearNativeLoginMaintenanceInfo}
        />
      )}
      {/* Native deep-link login that could not complete — without this the player
          is dropped back on the guest lobby with no explanation. Same full-screen
          notice, "Back to Login" returns to the lobby. */}
      {nativeLoginFailure && (
        <MaintenanceNotice
          info={{ title: nativeLoginFailure.title, displayMessage: nativeLoginFailure.displayMessage }}
          onBack={clearNativeLoginFailure}
        />
      )}
      {/* Failed/locked/turned-off biometric unlock (AC13/AC12/AC9) — keeps the
          player where they were instead of bouncing to the auth app. Outcomes
          arrive via subscribeUnlockPrompt from services/biometricLoginEntry. */}
      <BiometricUnlockPrompt />
      {/* Post-login biometrics offer (AC2) — mirrors the auth app's
          PasskeyEnrollmentModal so web and native prompt identically. */}
      <BiometricEnrollmentPrompt
        open={biometricOffer.show}
        biometryLabel={biometricOffer.biometryLabel}
        busy={biometricOffer.busy}
        enrolled={biometricOffer.enrolled}
        failed={biometricOffer.failed}
        onEnroll={biometricOffer.enroll}
        onSkip={biometricOffer.skip}
        onRetry={biometricOffer.retry}
        onConfirmationDone={biometricOffer.dismissConfirmation}
      />
      <Routes>
        {/* Legacy deep link redirect */}
        <Route path='/game/:gameID/type/:gameType' element={<GameDeepLinkRedirect />} />

        {/* Standalone seat/bet layout preview (no sockets/lobby). See pages/DemoTable. */}
        <Route path='/demo-table' element={<DemoTable />} />

        {/* Standalone hand-history preview (no live hand-history data yet). */}
        <Route path='/hand-history-playground' element={<HandHistoryPreview />} />

        {/* Unlisted diagnostics: public non-sensitive server config. Not linked
            anywhere — reachable only by knowing the URL. See pages/ConfigPage. */}
        <Route path='/config' element={<ConfigPage />} />

        {/* Main app. Not-logged-in visitors land on the guest lobby instead of
            being forced to log in; authenticated players get the full layout
            (lobby, table, grid). ViewManagerContext derives activeView from the URL. */}
        <Route
          path='/*'
          element={
            isGuest ? (
              // Guests get the same lobby↔table layout so they can open a table
              // and observe; the lobby panel is the guest lobby, and the table
              // panel is the standard Game view (observe mode, play actions
              // gated). Member-only auto-prompts (seat offers, promos) are
              // omitted.
              <CashierProvider>
                <ViewManagerProvider>
                  <NativeOrientationLock />
                  <BenchmarkTableBridge />
                  <SlideContainer
                    lobbyPanel={
                      <GameProvider>
                        <ScaleContextProvider>
                          <GuestLobby />
                        </ScaleContextProvider>
                      </GameProvider>
                    }
                    renderTablePanel={() => (
                      // GameProvider + ScaleContextProvider now live INSIDE Game,
                      // keyed by the displayed gameID, so RebindingTableView can
                      // keep the surface mounted and cross-fade table swaps while
                      // the per-table providers still reset per table.
                      <Game />
                    )}
                    gridPanel={<MultiTableGrid />}
                  />
                  {/* Support is available to guests in both the lobby and at a
                      table, so the panel is mounted here too. Rotated to match
                      the portrait-locked lobby on mobile-landscape; left in
                      landscape over a table. */}
                  <LobbyOverlayLock>
                    <SupportPanel open={supportOpen} onClose={closeSupport} />
                    {/* WORKSHOP TOOL — compiled out of production builds and lazy-loaded
                        elsewhere; see components/SfxLab/index.tsx for both locks. */}
                    <SfxLabMount />
                    <LobbyLabMount biometricPromptOpen={biometricPromptOpen} />
                  </LobbyOverlayLock>
                </ViewManagerProvider>
              </CashierProvider>
            ) : (
              <CashierProvider>
                <ViewManagerProvider>
                  <NativeOrientationLock />
                  <BenchmarkTableBridge />
                  <SlideContainer
                    lobbyPanel={
                      <GameProvider>
                        <ScaleContextProvider>
                          <Lobby />
                        </ScaleContextProvider>
                      </GameProvider>
                    }
                    renderTablePanel={() => (
                      // GameProvider + ScaleContextProvider now live INSIDE Game,
                      // keyed by the displayed gameID, so RebindingTableView can
                      // keep the surface mounted and cross-fade table swaps while
                      // the per-table providers still reset per table.
                      <Game />
                    )}
                    gridPanel={<MultiTableGrid />}
                  />
                  {/* Restores every OTHER table a guest had open, stashed by
                    utils/auth's captureOpenTablesBeforeLogin on login —
                    regardless of whether they were in Multi-Table View or a
                    single active table with background tables open. No-op
                    (renders null) when there's nothing left to restore. */}
                  <PostLoginTablesRestore />
                  {/* Exits tables of a tournament that is back to Announced (a
                    recurring MTT between runs) where the player has no seat. */}
                  <AnnouncedTournamentTableExit />
                  {/* EN-11109: starts the Sit & Go result / rematch offer in app
                    state, so it outlives the table panel. */}
                  <SitGoResultWatcher />
                  {/* Rotated to match the portrait-locked lobby on mobile-
                    landscape; left in landscape over a table. */}
                  <LobbyOverlayLock>
                    <CashierPanel />
                  </LobbyOverlayLock>
                  {/* The disruptive "Your Seat is Ready" modal is retired: a ready
                      seat now surfaces non-intrusively in the lobby "Your Tables"
                      strip (JoinReadyHand, shifted left-most) and, in-game, as a
                      JOIN countdown chip in the table nav row. The timeout toast
                      stays — it's a passive, non-blocking notice. */}
                  <SeatOfferTimeoutSnackbar />
                  {/* Audible "Table Ready" alert (saloon bell + burning fuse).
                      Headless + mounted once app-level so it fires regardless of
                      which table has focus, never stacks, and is deduped across
                      re-render / reconnect / refresh. See useWaitlistAlertSound. */}
                  <WaitlistAlertSound />
                  {/* Maintenance is surfaced by the scrolling MaintenanceMarquee
                    (lobby, table, and grid) rather than a full-screen page or a
                    recurring modal nag: the player keeps navigating and can
                    finish an in-progress hand, while gameplay/transactions are
                    gated separately (cash tiles dimmed, register/cashier/account
                    disabled). */}
                  {/* The one-time feature introductions share the promo's
                    login-modal slot — only one of them is ever presented in a
                    session — and never stack on the biometric enrollment offer
                    above. Newest first: the heads-up Sit & Go introduction
                    (EN-10748) decides, and mounts the multi-run one (EN-10791)
                    only once it has passed on the session. */}
                  <HeadsUpIntroAutoPrompt biometricPromptOpen={biometricPromptOpen}>
                    <MultiRunIntroAutoPrompt biometricPromptOpen={biometricPromptOpen} />
                  </HeadsUpIntroAutoPrompt>
                  <PromoEntryAutoPrompt />
                  <PromoWeeklyResetRefresh />
                  {/* Rotated to match the portrait-locked lobby on mobile-
                    landscape; left in landscape over a table. */}
                  <LobbyOverlayLock>
                    <SupportPanel open={supportOpen} onClose={closeSupport} />
                    {/* WORKSHOP TOOL — compiled out of production builds and lazy-loaded
                        elsewhere; see components/SfxLab/index.tsx for both locks. */}
                    <SfxLabMount />
                    <LobbyLabMount biometricPromptOpen={biometricPromptOpen} />
                  </LobbyOverlayLock>
                  {/* Mounted once at the app level so it sees the top-level
                    GlobalProvider's isDuplicateAccess flag set by App.tsx's
                    GOT_DUPLICATE_ACCESS listener. Per-cell <GlobalProvider>
                    wrappers in MultiTableGrid would otherwise shadow the
                    flag and hide the modal entirely in grid view. */}
                  <DuplicateScreenModal />
                  {/* Mounted once here (not per-cell in Game.tsx) and driven by
                    the redux errorMsg.open flag, so a join failure ("Unable to
                    take seat…") surfaces in single-table AND every grid cell
                    without duplicating across cells. */}
                  <ErrorMsgModal />
                  {/* EN-10064: colluder cash hard-block. Mounted app-level (like
                    ErrorMsgModal) so the integrity modal surfaces in every
                    view, driven by the redux colluderRestrictions.block flag. */}
                  <IntegrityBlockModal />
                </ViewManagerProvider>
              </CashierProvider>
            )
          }
        />
      </Routes>

      <AccessTokenExpiredMessageModal />
      <LoginRequiredModal />
      <DebugConsole />
    </>
  );
}

export default App;
